import { MOBILE_MVP_DURATION, resolveLandingFaqs, type LandingContent } from '@/lib/landingContent';

export type Locale = 'tr' | 'en';

type L = { tr: string; en: string };

export type ServiceFaqItem = {
  question: L;
  answer: L;
};

export type ServiceDefinition = {
  slug: string;
  shortLabel: L;
  navTitle: L;
  seoTitle: L;
  seoDescription: L;
  heroTitle: L;
  intro: L;
  idealFor: L[];
  problems: L[];
  deliverables: L[];
  engagementModels: L[];
  process: { title: L; description: L }[];
  differentiators: L[];
  fitFor: L[];
  notFitFor: L[];
  buyerQueries: string[];
  relatedProjectSlugs: string[];
  relatedBlogSlugs: string[];
  relatedToolIds: string[];
  taxonomy: {
    capabilities: string[];
    buyerTypes: string[];
    projectTypes: string[];
  };
  faqs: ServiceFaqItem[];
  /** Query-targeted landing blocks (quick answer, example bands, case studies). */
  landing?: LandingContent;
};

export function localizeServiceText(item: L, lang: Locale) {
  return item[lang];
}

const serviceDefinitions: ServiceDefinition[] = [
  {
    slug: 'ai-development',
    shortLabel: { tr: 'AI Development', en: 'AI Development' },
    navTitle: { tr: 'Yapay Zeka Entegrasyonu', en: 'AI Development' },
    seoTitle: {
      tr: 'Yapay Zeka Entegrasyonu | LLM, RAG ve AI Geliştirme',
      en: 'AI Development Company in Turkey | LLM, RAG & Agents',
    },
    seoDescription: {
      tr: 'Yapay zeka entegrasyonu: ChatGPT, Claude ve açık kaynak modelleri ürününüze, CRM/ERP’nize ve iş akışlarınıza bağlıyoruz. Süreç, örnek bant ve SSS.',
      en: 'AI development company in Istanbul, Turkey: LLM features, RAG and agent workflows built into your product and systems. Process, example bands and FAQ.',
    },
    heroTitle: {
      tr: 'Yapay zeka entegrasyonu: AI’ı mevcut ürününüze ve sistemlerinize bağlıyoruz.',
      en: 'AI development company in Turkey for production LLM features, RAG and agents.',
    },
    intro: {
      tr: 'Model seçimi, orchestration, guardrail ve ürün entegrasyonunu tek teslimat hattında yönetiyoruz.',
      en: 'We handle model strategy, orchestration, guardrails, and product integration in one delivery lane.',
    },
    idealFor: [
      { tr: 'Ürüne AI assistant/copilot eklemek isteyen SaaS ekipleri', en: 'SaaS teams adding AI assistants/copilots' },
      { tr: 'PoC sonrası production aşamasına geçmek isteyen startup’lar', en: 'Startups moving from PoC to production AI' },
      { tr: 'AI kalite/maliyet dengesini yönetmek isteyen ürün ekipleri', en: 'Product teams needing cost-quality AI control' },
      { tr: 'CRM, ERP veya destek aracına yapay zeka eklemek isteyen operasyon ekipleri', en: 'Operations teams adding AI to their CRM, ERP or support tools' },
      { tr: 'Türkiye dışından, Avrupa mesai saatleriyle örtüşen bir AI ekibi arayan şirketler', en: 'Companies outside Turkey looking for an AI team that overlaps European working hours' },
    ],
    problems: [
      { tr: 'PoC çalışıyor ama üretimde tutarlılık düşüyor', en: 'PoC works but production consistency fails' },
      { tr: 'Prompt, retrieval ve tool-call tek sistemde yönetilemiyor', en: 'Prompting, retrieval, and tool-calling are fragmented' },
      { tr: 'AI özellikleri ürün deneyimine bağlanamıyor', en: 'AI features are disconnected from product UX' },
    ],
    deliverables: [
      { tr: 'LLM özellikleri (chat, summary, extraction)', en: 'LLM features (chat, summarization, extraction)' },
      { tr: 'Agent workflow ve tool integration katmanı', en: 'Agent workflows and tool-integration layer' },
      { tr: 'Evaluation + monitoring + fallback setup', en: 'Evaluation, monitoring, and fallback setup' },
      { tr: 'Ürün API ve frontend entegrasyonu', en: 'Product API and frontend integration' },
    ],
    engagementModels: [
      { tr: 'Sıfırdan AI feature geliştirme', en: 'Build AI features from scratch' },
      { tr: 'Mevcut ürüne AI sprintleri', en: 'AI delivery sprints for existing products' },
      { tr: 'Embedded engineering partner modeli', en: 'Embedded engineering partner model' },
    ],
    process: [
      { title: { tr: 'Discovery', en: 'Discovery' }, description: { tr: 'Use-case, risk ve başarı metriklerini netleştiririz.', en: 'We define use cases, risks, and success metrics.' } },
      { title: { tr: 'Architecture', en: 'Architecture' }, description: { tr: 'Model, prompt, retrieval ve tool zincirini tasarlarız.', en: 'We design model, prompt, retrieval, and tool chain.' } },
      { title: { tr: 'Build', en: 'Build' }, description: { tr: 'Feature’i ürününüzde çalışan şekilde geliştiririz.', en: 'We implement production-grade features in your product.' } },
      { title: { tr: 'Hardening', en: 'Hardening' }, description: { tr: 'Kalite, gözlemlenebilirlik ve maliyet kontrolü ekleriz.', en: 'We harden with observability, quality, and cost control.' } },
    ],
    differentiators: [
      { tr: 'AI + ürün + mühendisliği aynı ritimde yürütürüz', en: 'We align AI, product, and engineering in one cadence' },
      { tr: 'Demo değil, bakım yapılabilir production sistem teslim ederiz', en: 'We deliver maintainable production systems, not demos' },
      { tr: 'Global ekiplerle remote/async çalışmaya uyumluyuz', en: 'We are remote/async-ready for global teams' },
    ],
    fitFor: [
      { tr: 'Net kullanıcı değeri tanımlı AI kullanım senaryoları', en: 'AI use cases tied to clear user value' },
      { tr: 'Kısa iterasyonlarla öğrenen ürün ekipleri', en: 'Product teams that iterate and learn quickly' },
    ],
    notFitFor: [
      { tr: 'Sadece “AI var” demek için yapılan projeler', en: 'Projects adding AI only for optics' },
      { tr: 'Kalite standardı olmadan acele teslim beklentisi', en: 'Rush delivery requests without quality standards' },
    ],
    buyerQueries: ['ai development partner', 'ai agent builders', 'llm integration company'],
    relatedProjectSlugs: ['ztech', 'lcw'],
    relatedBlogSlugs: ['ai-development-company-selection-guide', 'chatgpt-integration-business-guide', 'evaluation-debt-in-ai-product-features'],
    relatedToolIds: ['customer-reply-generator', 'quote-proposal-writer', 'job-posting-writer'],
    taxonomy: {
      capabilities: ['LLM Integration', 'Agent Workflows', 'AI Product Engineering'],
      buyerTypes: ['SaaS Teams', 'Founders', 'Engineering Leads'],
      projectTypes: ['AI Assistants', 'Workflow Automation', 'AI Product Features'],
    },
    faqs: [
      {
        question: { tr: 'Mevcut ürüne sonradan AI ekleyebilir misiniz?', en: 'Can you add AI to an existing product?' },
        answer: { tr: 'Evet. Mevcut mimariyi analiz edip AI özelliklerini düşük riskle kademeli entegre ediyoruz.', en: 'Yes. We audit the current architecture and integrate AI features incrementally with low migration risk.' },
      },
      {
        question: { tr: 'Model seçimi ve maliyet optimizasyonu da dahil mi?', en: 'Do you handle model strategy and cost optimization?' },
        answer: { tr: 'Evet. Kalite, gecikme ve maliyet dengesine göre model ve runtime stratejisi kuruyoruz.', en: 'Yes. We set model/runtime strategy around quality, latency, and cost tradeoffs.' },
      },
      {
        question: { tr: 'Sadece danışmanlık mı, yoksa geliştirme de var mı?', en: 'Do you only consult, or also build?' },
        answer: { tr: 'Danışmanlıktan üretim teslimatına kadar çalışıyoruz; ekibinizle embedded model de mümkün.', en: 'We work from strategy through production delivery, including embedded execution with your team.' },
      },
      {
        question: { tr: 'Yapay zeka entegrasyonu ne kadar sürer ve ne kadar tutar?', en: 'How long does AI development take, and what does it cost?' },
        answer: {
          tr: 'Maliyet tahmincimizde AI için ayrı bir profil yok; en yakın profil olan genel / özel yazılım MVP bandı {price:general.mvp}, {duration:general.mvp} (örnek bant, KDV hariç). Kesin fiyat ve süre, proje analizinden sonra iş listesi, efor ve kurulan outsource ekibe göre yazılı teklifte belirlenir.',
          en: 'Our cost estimator has no separate AI profile; the closest one, the general / custom software MVP band, is {price:general.mvp} over {duration:general.mvp} (example band, excl. VAT). The final price and timeline are set in a written proposal after the project analysis, from the task list, the effort and the outsourced team assembled for it.',
        },
      },
      {
        question: { tr: 'Verilerimiz model sağlayıcıya gider mi? KVKK ve GDPR nasıl ele alınıyor?', en: 'Does our data go to the model provider? How are KVKK and GDPR handled?' },
        answer: {
          tr: 'Modele hangi verinin gideceği proje analizinde belirlenir. Kişisel verinin azaltılması veya maskelenmesi, erişim kayıtları ve model sağlayıcının veri kullanım koşulları teklif aşamasında yazılı hale getirilir; kişisel veri işleniyorsa veri işleme sözleşmesi yapılır.',
          en: 'Which data reaches the model is decided in the project analysis. Personal-data minimisation or masking, access logging and the model provider’s data-use terms are written down at proposal stage; where personal data is processed, a data processing agreement is put in place.',
        },
      },
      {
        question: { tr: 'Türkiye dışındaki şirketlerle çalışıyor musunuz?', en: 'Do you work with companies outside Turkey?' },
        answer: {
          tr: 'Evet. İstanbul merkezliyiz (UTC+3) ve Türkiye’nin tüm şehirlerindeki ve yurt dışındaki müşterilerle uzaktan çalışıyoruz; iletişim ve dokümantasyon İngilizce de yürütülebilir.',
          en: 'Yes. We are based in Istanbul (UTC+3) and work remotely with clients in every city in Turkey and in other countries; communication and documentation run in English.',
        },
      },
    ],
    landing: {
      quickAnswer: {
        tr: 'Yapay zeka entegrasyonu; ChatGPT, Claude veya açık kaynak modelleri mevcut yazılımınıza, CRM ya da ERP sisteminize ve iş akışlarınıza güvenli şekilde bağlamaktır. Figtures kullanım senaryosunu, veri kaynaklarını ve KVKK gereksinimlerini netleştirir, eforu hesaplar ve projeye özel outsource ekip kurar. Örnek bant {price:general.mvp}, {duration:general.mvp}; KDV hariçtir, kesin fiyat proje analizinden sonra belirlenir.',
        en: 'Figtures is an AI development company based in Istanbul, Turkey. We build LLM features, RAG search and agent workflows into existing products, connected to your CRM, ERP and data. Each project is scoped from a task list and effort estimate and staffed with an outsourced team. Example band: {price:general.mvp} over {duration:general.mvp}, excl. VAT; final price after project analysis.',
      },
      costBands: [
        { profile: 'general', scope: 'mvp' },
        { profile: 'general', scope: 'platform' },
      ],
      costBandNote: {
        tr: 'Maliyet tahmincisinde yapay zeka entegrasyonu için ayrı bir profil yoktur; gösterilen bantlar en yakın profil olan “Genel / özel yazılım” bantlarıdır. Gerçek fiyat, entegrasyonun kapsamına göre proje analizinden sonra belirlenir.',
        en: 'The cost estimator has no separate profile for AI work; the bands shown are from the closest profile, “General / custom software”. The actual price depends on the integration scope and is set after the project analysis.',
      },
      caseStudies: ['p3'],
      caseStudyNote: {
        tr: 'Outfica’da Figtures ürün ve arayüz tasarımını yaptı; yapay zeka modelleri Outfica’ya aittir. Case study sayfası Figtures’ın rolünü ayrıca açıklar.',
        en: 'On Outfica, Figtures did the product and interface design; the AI models belong to Outfica. The case study page sets out Figtures’ role separately.',
      },
      sections: [
        {
          title: { tr: 'Hangi entegrasyonları yapıyoruz?', en: 'What we integrate' },
          items: [
            { tr: 'OpenAI (ChatGPT), Anthropic (Claude) ve açık kaynak modellerin API üzerinden ürüne bağlanması', en: 'OpenAI (ChatGPT), Anthropic (Claude) and open-source models connected to your product through their APIs' },
            { tr: 'Doküman ve bilgi tabanınız üzerinde kaynak gösteren arama ve cevaplama (RAG)', en: 'Retrieval-augmented answers (RAG) that cite your documents and knowledge base' },
            { tr: 'CRM, ERP, destek ve iç araçlarla veri alışverişi yapan entegrasyon katmanı', en: 'An integration layer exchanging data with CRM, ERP, helpdesk and internal tools' },
            { tr: 'İnsan onaylı agent ve otomasyon akışları', en: 'Agent and automation workflows with human approval steps' },
          ],
        },
        {
          title: { tr: 'Güvenlik, KVKK ve GDPR', en: 'Security, KVKK and GDPR' },
          items: [
            { tr: 'Modele gönderilen verinin kapsamı ve maskeleme kuralları analizde yazılı hale gelir', en: 'The data sent to the model and its masking rules are written down during analysis' },
            { tr: 'Erişim kayıtları, yetkilendirme ve maliyet/kalite izleme production kapsamındadır', en: 'Access logs, permissions and cost/quality monitoring are part of the production scope' },
            { tr: 'Kişisel veri işleniyorsa veri işleme sözleşmesi ve barındırma bölgesi sözleşmede netleşir', en: 'Where personal data is processed, the data processing agreement and hosting region are set in the contract' },
          ],
        },
      ],
    },
  },
  {
    slug: 'custom-software-development',
    shortLabel: { tr: 'Custom Software', en: 'Custom Software' },
    navTitle: { tr: 'Özel Yazılım Geliştirme', en: 'Custom Software Development' },
    seoTitle: {
      tr: 'Custom Software Product Studio | Kurumsal Yazılım Geliştirme',
      en: 'Custom Software Product Studio | Product-Grade Engineering',
    },
    seoDescription: {
      tr: 'Operasyonunuza özel web platformları, backend ve entegrasyonlar — bakım yapılabilir, test edilebilir, production-ready custom software.',
      en: 'Custom web platforms, backends, and integrations built to production standards—maintainable, tested, and operationally ready.',
    },
    heroTitle: {
      tr: 'Hazır araçların yetmediği yerde, işinize özel yazılım geliştiriyoruz.',
      en: 'When off-the-shelf tools fall short, we build software tailored to your workflows.',
    },
    intro: {
      tr: 'Özel süreçlerinizi destekleyen sistemler tasarlıyor, geliştiriyor ve sürdürülebilir hale getiriyoruz.',
      en: 'We design, build, and harden systems that support your unique business operations.',
    },
    idealFor: [
      { tr: 'Özel operasyon akışları olan ekipler', en: 'Teams with unique operational workflows' },
      { tr: 'Legacy sistemini modernleştirmek isteyen şirketler', en: 'Companies modernizing legacy systems' },
      { tr: 'Bakımı kolay, ölçeklenebilir ürün isteyen organizasyonlar', en: 'Organizations needing scalable, maintainable systems' },
    ],
    problems: [
      { tr: 'Farklı araçlar arasında kopuk süreçler', en: 'Fragmented workflows across disconnected tools' },
      { tr: 'Ölçeklendikçe bozulan backend performansı', en: 'Backend performance degradation under scale' },
      { tr: 'Değişiklik maliyeti yüksek kod tabanı', en: 'High-change-cost codebase' },
    ],
    deliverables: [
      { tr: 'Özel web uygulamaları ve internal platformlar', en: 'Custom web apps and internal platforms' },
      { tr: 'API-first backend servisleri', en: 'API-first backend services' },
      { tr: 'Admin paneli ve rol/yetki katmanı', en: 'Admin panel and role/permission architecture' },
      { tr: 'Test + release + bakım süreçleri', en: 'Testing, release, and maintenance workflows' },
    ],
    engagementModels: [
      { tr: 'Discovery + delivery sprint modeli', en: 'Discovery + delivery sprint model' },
      { tr: 'Kademeli modernizasyon fazları', en: 'Phased modernization model' },
      { tr: 'Product team extension yaklaşımı', en: 'Product team extension approach' },
    ],
    process: [
      { title: { tr: 'Scope', en: 'Scope' }, description: { tr: 'Hedefleri, teknik sınırları ve başarı ölçümünü netleştiririz.', en: 'We define goals, technical boundaries, and success metrics.' } },
      { title: { tr: 'Design', en: 'Design' }, description: { tr: 'Bilgi mimarisi ve sistem sınırlarını kurarız.', en: 'We design information architecture and system boundaries.' } },
      { title: { tr: 'Build', en: 'Build' }, description: { tr: 'Modüler ve test edilebilir şekilde geliştiririz.', en: 'We implement in a modular and testable structure.' } },
      { title: { tr: 'Iterate', en: 'Iterate' }, description: { tr: 'Kullanım verisine göre optimize ederiz.', en: 'We optimize iteratively with usage signals.' } },
    ],
    differentiators: [
      { tr: 'Sadece feature değil, ürün sistemi teslim ederiz', en: 'We deliver a product system, not isolated features' },
      { tr: 'Tasarım ve mühendisliği aynı hat üzerinde çalıştırırız', en: 'We align design and engineering in one lane' },
      { tr: 'Global ekiplerle planlı ve şeffaf iletişim kurarız', en: 'We collaborate transparently with global teams' },
    ],
    fitFor: [
      { tr: 'Operasyonel değeri ölçülebilir yazılım projeleri', en: 'Software projects with measurable business outcomes' },
      { tr: 'Uzun vadeli teknik sahiplik gerektiren ürünler', en: 'Products requiring long-term technical ownership' },
    ],
    notFitFor: [
      { tr: 'Bakım planı olmayan kısa ömürlü demo işleri', en: 'Short-lived demo projects with no maintenance plan' },
      { tr: 'Ürün öncelikleri net olmayan talepler', en: 'Requests without clear product priorities' },
    ],
    buyerQueries: ['software development company', 'custom software team', 'web app development partner'],
    relatedProjectSlugs: ['ailem-aselsan', 'lcw'],
    relatedBlogSlugs: ['api-contract-evolution-without-breakage', 'developer-experience-as-architecture'],
    relatedToolIds: ['project-cost-estimator', 'customer-reply-generator'],
    taxonomy: {
      capabilities: ['Web Applications', 'Backend Systems', 'Platform Modernization'],
      buyerTypes: ['Operations Teams', 'Product Leaders', 'CTOs'],
      projectTypes: ['Internal Platforms', 'B2B Systems', 'Operational Dashboards'],
    },
    faqs: [
      {
        question: { tr: 'Mevcut kod tabanını devralıp geliştirebilir misiniz?', en: 'Can you take over and improve our existing codebase?' },
        answer: { tr: 'Evet. Kod tabanı denetimi sonrası güvenli modernizasyon ve yeni özellik planı çıkarıyoruz.', en: 'Yes. We audit the codebase and execute safe modernization plus feature delivery.' },
      },
      {
        question: { tr: 'Lansman sonrası destek veriyor musunuz?', en: 'Do you provide post-launch support?' },
        answer: { tr: 'Evet. Bakım, iyileştirme ve yeni sprintlerle ürün gelişimini sürdürüyoruz.', en: 'Yes. We continue with maintenance, optimization, and iterative feature sprints.' },
      },
      {
        question: { tr: 'Sadece geliştirme değil, teknik karar desteği de veriyor musunuz?', en: 'Do you support technical decision-making as well?' },
        answer: { tr: 'Evet. Mimari ve ölçeklenme kararlarını ürün hedefleriyle birlikte ele alıyoruz.', en: 'Yes. We support architecture and scaling decisions aligned with product goals.' },
      },
      {
        question: { tr: 'Hangi sektör ve şehirlere hizmet veriyorsunuz, fiyat nasıl belirleniyor?', en: 'Which sectors and locations do you serve, and how is pricing set?' },
        answer: {
          tr: 'Her sektörde her türlü yazılım işini üstleniyor; Türkiye’nin tüm şehirlerine ve yurt dışına uzaktan hizmet veriyoruz. Fiyat proje bazında belirlenir: projeyi analiz ediyor, efora göre outsource ekip kuruyor ve fiyatı bu efor ve ekip maliyetinden hesaplayıp yazılı teklifle paylaşıyoruz.',
          en: 'We take on every kind of software work in every sector and serve every city in Turkey and clients abroad remotely. Pricing is set per project: we analyse the project, assemble an outsourced team from the effort, and calculate the price from that effort and team cost in a written proposal.',
        },
      },
    ],
  },
  {
    slug: 'mobile-app-development',
    shortLabel: { tr: 'Mobile Apps', en: 'Mobile Apps' },
    navTitle: { tr: 'Mobil Uygulama Geliştirme', en: 'Mobile App Development' },
    seoTitle: {
      tr: 'Mobil Uygulama Geliştirme Firması | iOS ve Android',
      en: 'Mobile App Development Company | iOS & Android Apps',
    },
    seoDescription: {
      tr: 'İstanbul merkezli mobil uygulama geliştirme firması: iOS ve Android uygulamaları tasarımdan mağaza yayınına. Mobil MVP projeye göre 1–2 haftadan 6–8 haftaya.',
      en: 'Istanbul-based mobile app development company: iOS and Android apps from design to store release. A mobile MVP takes 1–2 to 6–8 weeks, depending on the project.',
    },
    heroTitle: {
      tr: 'Mobil uygulama geliştirme firması: iOS ve Android uygulamanızı mağazaya kadar taşıyoruz.',
      en: 'Mobile app development company: we take your iOS and Android app all the way to the stores.',
    },
    intro: {
      tr: 'MVP’den büyüme aşamasına kadar mobil deneyimi, ürün ve mühendislik birlikte ele alıyoruz.',
      en: 'From MVP to scale, we build mobile experiences through aligned product and engineering.',
    },
    idealFor: [
      { tr: 'Mobilde ürün lansmanı yapmak isteyen startup’lar', en: 'Startups launching mobile products' },
      { tr: 'Mevcut mobil uygulamasını iyileştirmek isteyen ekipler', en: 'Teams improving existing mobile apps' },
      { tr: 'Web ürününü mobile genişleten SaaS şirketleri', en: 'SaaS teams extending web products to mobile' },
    ],
    problems: [
      { tr: 'Düşük performans ve kırılgan release süreci', en: 'Performance issues and fragile release process' },
      { tr: 'Karmaşık onboarding ve düşük aktivasyon', en: 'Complex onboarding and weak activation' },
      { tr: 'Mobil-backend entegrasyon sorunları', en: 'Mobile-backend integration issues' },
    ],
    deliverables: [
      { tr: 'iOS + Android uygulama geliştirme', en: 'iOS + Android development' },
      { tr: 'Mobil ürün mimarisi ve state yönetimi', en: 'Mobile architecture and state management' },
      { tr: 'Auth, analytics, push, ödeme entegrasyonları', en: 'Auth, analytics, push, and payment integrations' },
      { tr: 'Store release ve post-launch iterasyon', en: 'Store release and post-launch iteration' },
    ],
    engagementModels: [
      { tr: 'MVP mobil sprintleri', en: 'MVP mobile sprints' },
      { tr: 'Mevcut app modernizasyonu', en: 'Existing app modernization' },
      { tr: 'Embedded mobile partner modeli', en: 'Embedded mobile partner model' },
    ],
    process: [
      { title: { tr: 'Scope', en: 'Scope' }, description: { tr: 'Kritik mobil akışları önceliklendiririz.', en: 'We prioritize the critical mobile journeys.' } },
      { title: { tr: 'Design', en: 'Design' }, description: { tr: 'Kullanılabilir ve ölçülebilir UX tasarlarız.', en: 'We design usable, measurable UX flows.' } },
      { title: { tr: 'Build', en: 'Build' }, description: { tr: 'Performans odaklı uygulama geliştiririz.', en: 'We build performance-focused mobile apps.' } },
      { title: { tr: 'Improve', en: 'Improve' }, description: { tr: 'Kullanım verisiyle ürün iterasyonu yaparız.', en: 'We iterate with usage and product signals.' } },
    ],
    differentiators: [
      { tr: 'Mobili ürün sisteminin parçası olarak ele alırız', en: 'We treat mobile as part of the product system' },
      { tr: 'Tasarım ve geliştirmeyi tek sprint ritminde yürütürüz', en: 'We align design and engineering in one sprint rhythm' },
      { tr: 'Global ekiplerle async çalışma pratiğimiz güçlü', en: 'We collaborate effectively with global async teams' },
    ],
    fitFor: [
      { tr: 'Mobili büyüme kanalı olarak kullanan ürünler', en: 'Products where mobile is a growth channel' },
      { tr: 'Lansman sonrası düzenli iterasyon planı olan ekipler', en: 'Teams committed to post-launch iteration' },
    ],
    notFitFor: [
      { tr: 'Bakım planı olmayan tek seferlik teslim talepleri', en: 'One-off delivery with no maintenance plan' },
      { tr: 'Kullanıcı akışı tanımlanmamış projeler', en: 'Projects without defined user journeys' },
    ],
    buyerQueries: ['mobile app development agency', 'ios android development team', 'startup app developers'],
    relatedProjectSlugs: ['ailem-aselsan', 'kolektif'],
    relatedBlogSlugs: ['mobile-app-development-cost-guide', 'mobile-app-performance-optimization', 'offline-first-mobile-sync-conflict-discipline'],
    relatedToolIds: ['brand-kit-generator', 'project-cost-estimator'],
    taxonomy: {
      capabilities: ['Mobile Engineering', 'React Native', 'Mobile UX'],
      buyerTypes: ['Founders', 'Product Managers', 'Mobile Teams'],
      projectTypes: ['MVP Mobile Apps', 'Consumer Apps', 'B2B Companion Apps'],
    },
    faqs: [
      {
        question: { tr: 'Hem iOS hem Android için tek ekip olarak ilerleyebilir misiniz?', en: 'Can one team handle both iOS and Android?' },
        answer: { tr: 'Evet. Ürün hedeflerinize göre tek planla her iki platformu da yönetiyoruz.', en: 'Yes. We manage both platforms with one aligned product plan.' },
      },
      {
        question: { tr: 'Lansman sonrası destek sunuyor musunuz?', en: 'Do you provide post-launch support?' },
        answer: { tr: 'Evet. Performans, stabilite ve yeni özellik iterasyonlarıyla devam ediyoruz.', en: 'Yes. We continue with stability, performance, and feature iterations.' },
      },
      {
        question: { tr: 'Mobil proje için ürün ve UX desteği de veriyor musunuz?', en: 'Do you also support product direction and UX for mobile?' },
        answer: { tr: 'Evet. Teknik geliştirmeyi ürün hedefleri ve UX kararlarıyla birlikte yürütüyoruz.', en: 'Yes. We align engineering execution with product goals and UX decisions.' },
      },
      {
        question: { tr: 'Bir mobil MVP ne kadar sürer?', en: 'How long does a mobile MVP take?' },
        answer: {
          tr: 'Projeye göre bir mobil MVP 1–2 haftadan 6–8 haftaya kadar sürer. Süre; ekran ve akış sayısına, backend ve entegrasyon ihtiyacına ve mağaza yayın sürecine göre proje analizinde netleşir ve yazılı teklifte yer alır.',
          en: 'Depending on the project, a mobile MVP takes from 1–2 weeks to 6–8 weeks. The timeline depends on the number of screens and flows, backend and integration needs, and the store release process; it is confirmed in the project analysis and stated in the written proposal.',
        },
      },
      {
        question: { tr: 'Mobil uygulama geliştirme ne kadar tutar?', en: 'How much does mobile app development cost?' },
        answer: {
          tr: 'Maliyet tahmincimizdeki örnek bantlar: mobil MVP {price:mobile_product.mvp}, genişletilmiş platform {price:mobile_product.platform} (KDV hariç). Sabit fiyat listemiz yoktur; kesin fiyat proje analizinden sonra iş listesi, efor ve kurulan outsource ekibin maliyetiyle yazılı teklifte belirlenir.',
          en: 'Example bands from our cost estimator: mobile MVP {price:mobile_product.mvp}, expanded platform {price:mobile_product.platform} (excl. VAT). There is no fixed price list; the final price is set in a written proposal after the project analysis, from the task list, the effort and the cost of the outsourced team.',
        },
      },
      {
        question: { tr: 'Kaynak kod ve mağaza hesapları kimde kalır?', en: 'Who owns the source code and the store accounts?' },
        answer: {
          tr: 'Kapsam, lisans ve fikri mülkiyet imzalı teklif ve hizmet sözleşmesinde belirlenir. Devirde kaynak kod, depo erişimi, dokümantasyon ve mağaza, hosting ve üçüncü taraf hesaplarının erişim devri yer alır.',
          en: 'Scope, licensing and intellectual property are set out in the signed proposal and service agreement. The handover includes source code, repository access, documentation, and access transfer for store, hosting and third-party accounts.',
        },
      },
      {
        question: { tr: 'Hangi şehirlerdeki firmalarla çalışıyorsunuz?', en: 'Which locations do you work with?' },
        answer: {
          tr: 'Merkezimiz İstanbul’da; Türkiye’nin tüm şehirlerindeki ve yurt dışındaki müşterilerle uzaktan çalışıyoruz. Toplantılar online yürür, ilerleme düzenli demolarla paylaşılır.',
          en: 'We are based in Istanbul and work remotely with clients in every city in Turkey and in other countries. Meetings run online and progress is shared through regular demos.',
        },
      },
    ],
    landing: {
      quickAnswer: {
        tr: 'Figtures, iOS ve Android uygulamalarını tasarlayıp mağaza yayınına kadar taşıyan İstanbul merkezli bir mobil uygulama geliştirme firmasıdır. Talebinizi dinler, ekran ve akış listesini çıkarır, eforu hesaplar ve projeye özel outsource ekip kurarız. Mobil MVP projeye göre 1–2 haftadan 6–8 haftaya sürer; örnek bant {price:mobile_product.mvp}, KDV hariç. Kesin fiyat proje analizinden sonra belirlenir.',
        en: 'Figtures is an Istanbul-based mobile app development company that designs and ships iOS and Android apps through to store release. We list the screens and flows, estimate the effort and assemble an outsourced team for your project. A mobile MVP takes 1–2 weeks to 6–8 weeks depending on the project; example band {price:mobile_product.mvp}, excl. VAT, final price after project analysis.',
      },
      costBands: [
        { profile: 'mobile_product', scope: 'mvp', duration: MOBILE_MVP_DURATION },
        { profile: 'mobile_product', scope: 'platform' },
      ],
      caseStudies: ['p3', 'p10'],
      caseStudyNote: {
        tr: 'Henüz yayınlanmış bir mobil uygulama case study’miz yok. Aşağıdaki yayındaki projeler, mobil projelerde de uyguladığımız ürün tasarımı ve teslimat yaklaşımını gösterir.',
        en: 'We have not published a mobile app case study yet. The published projects below show the product design and delivery approach we also apply to mobile work.',
      },
      sections: [
        {
          title: { tr: 'Mobil uygulama firması seçerken sorun', en: 'Questions to ask a mobile app company' },
          items: [
            { tr: 'Kaynak kod, depo ve mağaza hesapları devirde kime ait olacak?', en: 'Who will own the source code, repository and store accounts after handover?' },
            { tr: 'Mağaza incelemesi ve red durumlarını kim yönetiyor?', en: 'Who handles store review and rejections?' },
            { tr: 'Backend, entegrasyon ve yönetim paneli kapsama dahil mi?', en: 'Are the backend, integrations and admin panel in scope?' },
            { tr: 'Yayın sonrası bakım ve destek nasıl kapsamlanıyor?', en: 'How are post-launch maintenance and support scoped?' },
          ],
        },
        {
          title: { tr: 'Mağaza yayını ve sonrası', en: 'Store release and after' },
          items: [
            { tr: 'App Store ve Google Play metinleri, ekran görüntüleri ve gizlilik beyanları', en: 'App Store and Google Play listings, screenshots and privacy declarations' },
            { tr: 'Hata takibi ve ürün analitiği kurulumu', en: 'Crash reporting and product analytics setup' },
            { tr: 'Bakım ve destek, süresi ve kapsamı teklifte yazılı olarak ayrıca planlanır', en: 'Maintenance and support planned separately, with duration and coverage written into the proposal' },
          ],
        },
      ],
    },
  },
  {
    slug: 'web-development',
    shortLabel: { tr: 'Web Development', en: 'Web Development' },
    navTitle: { tr: 'Web Geliştirme', en: 'Web Development' },
    seoTitle: {
      tr: 'Web Uygulama Product Studio | Full-Stack Web Geliştirme',
      en: 'Web Application Product Studio | Full-Stack Product Engineering',
    },
    seoDescription: {
      tr: 'Core Web Vitals odaklı web uygulamaları, dashboard ve platformlar — SEO, erişilebilirlik ve ölçeklenebilir mimari ile.',
      en: 'Core Web Vitals–focused web apps, dashboards, and platforms—with SEO, accessibility, and scalable architecture.',
    },
    heroTitle: {
      tr: 'Web ürünlerinizi hızlı, güvenilir ve ölçeklenebilir şekilde geliştiriyoruz.',
      en: 'We build web products that are fast, reliable, and scalable.',
    },
    intro: {
      tr: 'Landing page’den kompleks web platformuna kadar performans, UX ve teslimatı birlikte optimize ediyoruz.',
      en: 'From landing pages to complex web platforms, we optimize performance, UX, and delivery together.',
    },
    idealFor: [
      { tr: 'Yeni web ürün lansmanı yapan startup’lar', en: 'Startups launching new web products' },
      { tr: 'Platformunu modernleştirmek isteyen ekipler', en: 'Teams modernizing existing platforms' },
      { tr: 'Büyüme odaklı SaaS web ekipleri', en: 'Growth-focused SaaS web teams' },
    ],
    problems: [
      { tr: 'Yavaş yüklenen ve düşük dönüşüm üreten deneyimler', en: 'Slow, low-conversion web experiences' },
      { tr: 'Ölçekte kırılan frontend/backend entegrasyonu', en: 'Fragile frontend/backend integration at scale' },
      { tr: 'Bakımı zor UI ve release süreçleri', en: 'Hard-to-maintain UI and release workflows' },
    ],
    deliverables: [
      { tr: 'Kurumsal web sitesi ve ürün landing page’leri', en: 'Corporate sites and product landing pages' },
      { tr: 'SaaS dashboard ve admin panel geliştirme', en: 'SaaS dashboards and admin panel development' },
      { tr: 'SEO + performans odaklı frontend altyapısı', en: 'SEO and performance-focused frontend architecture' },
      { tr: 'Backend API ve entegrasyon katmanı', en: 'Backend API and integration layer' },
    ],
    engagementModels: [
      { tr: 'Yeni web ürün geliştirme', en: 'New web product build' },
      { tr: 'Refactor + modernizasyon modeli', en: 'Refactor and modernization model' },
      { tr: 'Sürekli growth sprint yaklaşımı', en: 'Continuous growth sprint approach' },
    ],
    process: [
      { title: { tr: 'Audit', en: 'Audit' }, description: { tr: 'Teknik ve içerik performansını analiz ederiz.', en: 'We audit technical and content performance.' } },
      { title: { tr: 'Plan', en: 'Plan' }, description: { tr: 'Öncelikli akışları ve roadmap’i belirleriz.', en: 'We define priority journeys and roadmap.' } },
      { title: { tr: 'Build', en: 'Build' }, description: { tr: 'Tasarım ve geliştirmeyi birlikte yürütürüz.', en: 'We execute design and engineering together.' } },
      { title: { tr: 'Optimize', en: 'Optimize' }, description: { tr: 'Kullanım verisiyle performans ve dönüşümü iyileştiririz.', en: 'We optimize conversion and performance with usage data.' } },
    ],
    differentiators: [
      { tr: 'Sadece görünen yüzü değil, teknik temelini de güçlendiririz', en: 'We improve both UX surface and technical foundation' },
      { tr: 'SEO, performans ve ürün deneyimini tek çerçevede ele alırız', en: 'We align SEO, performance, and product UX in one framework' },
      { tr: 'Global ekiplerle düzenli çalışma ritmi kurarız', en: 'We establish steady collaboration with global teams' },
    ],
    fitFor: [
      { tr: 'Dönüşüm odaklı web büyüme hedefi olan ürünler', en: 'Products with conversion-focused web growth goals' },
      { tr: 'Sürekli iyileştirme ritmi olan ekipler', en: 'Teams with continuous improvement cadence' },
    ],
    notFitFor: [
      { tr: 'Kalite/test kriteri olmadan hızlı teslim beklentisi', en: 'Rush delivery requests without quality/testing criteria' },
      { tr: 'Kullanıcı ve iş sonucu tanımı olmayan projeler', en: 'Projects without user/business outcome definition' },
    ],
    buyerQueries: ['web development partner', 'web app development company', 'saas web engineering team'],
    relatedProjectSlugs: ['ztech', 'lcw'],
    relatedBlogSlugs: ['react-conc-2025', 'api-contract-evolution-without-breakage'],
    relatedToolIds: ['instagram-caption-generator', 'youtube-seo-generator'],
    taxonomy: {
      capabilities: ['Frontend Engineering', 'Backend APIs', 'Web Performance'],
      buyerTypes: ['Marketing + Product Teams', 'SaaS Founders', 'Engineering Leads'],
      projectTypes: ['Product Websites', 'SaaS Platforms', 'Operational Dashboards'],
    },
    faqs: [
      {
        question: { tr: 'Hem kurumsal site hem ürün dashboard geliştirebilir misiniz?', en: 'Can you build both a website and product dashboards?' },
        answer: { tr: 'Evet. Pazarlama sitesi ve ürün dashboard katmanlarını aynı mimari yaklaşımda teslim ediyoruz.', en: 'Yes. We deliver marketing and product layers within one coherent architecture.' },
      },
      {
        question: { tr: 'Mevcut web projemizi performans ve SEO için iyileştirebilir misiniz?', en: 'Can you improve our existing web project for performance and SEO?' },
        answer: { tr: 'Evet. Teknik audit sonrası performans, bilgi mimarisi ve içerik sunumunda iyileştirme uygularız.', en: 'Yes. We run a technical audit and implement targeted improvements for performance and content architecture.' },
      },
      {
        question: { tr: 'Lansman sonrası growth iterasyonlarıyla devam ediyor musunuz?', en: 'Do you continue with growth iterations after launch?' },
        answer: { tr: 'Evet. Lansman sonrası içerik, UX ve dönüşüm odaklı sprintlerle devam edebiliriz.', en: 'Yes. We continue with content, UX, and conversion-focused post-launch sprints.' },
      },
    ],
  },
  {
    slug: 'saas-mvp-development',
    shortLabel: { tr: 'SaaS', en: 'SaaS MVP' },
    navTitle: { tr: 'SaaS Geliştirme', en: 'SaaS MVP Development' },
    seoTitle: {
      tr: 'SaaS Geliştirme | Abonelikli Web Ürünü ve SaaS MVP',
      en: 'SaaS MVP Development Agency | Founders & Product Teams',
    },
    seoDescription: {
      tr: 'SaaS geliştirme: kullanıcı ve rol yönetimi, abonelik ve ödeme, çok kiracılı veri yapısı ve yönetim paneliyle ürününüzü yayına alıyoruz. Örnek bant ve SSS.',
      en: 'SaaS MVP development agency for founders: auth, billing, multi-tenant data and admin, scoped from a task list and effort estimate. Example bands and FAQ.',
    },
    heroTitle: {
      tr: 'SaaS geliştirme: abonelikli ürününüzü fikirden yayına taşıyoruz.',
      en: 'SaaS MVP development agency: from idea to a launch-ready subscription product.',
    },
    intro: {
      tr: 'İlk kullanıcıdan ürün-market fit iterasyonuna kadar startup ekipleriyle yakın çalışıyoruz.',
      en: 'We work closely with startups from first-user launch through product-market-fit iterations.',
    },
    idealFor: [
      { tr: 'Hızlı MVP çıkmak isteyen kurucu ekipler', en: 'Founder teams needing fast MVP launch' },
      { tr: 'İlk kullanıcı geri bildirimi toplamak isteyen SaaS startup’lar', en: 'SaaS startups validating with first real users' },
      { tr: 'İç kapasitesi sınırlı ama hedefi net ekipler', en: 'Teams with clear goals and limited internal capacity' },
    ],
    problems: [
      { tr: 'Önceliklendirme yapılmadan şişen MVP kapsamı', en: 'MVP scope bloating without prioritization' },
      { tr: 'Hızlı çıkan ama ölçeklenemeyen teknik temel', en: 'Fast launch but non-scalable technical base' },
      { tr: 'Ürün kararları ve teknik teslimatın ayrışması', en: 'Product decisions disconnected from technical delivery' },
    ],
    deliverables: [
      { tr: 'MVP kapsamı, teknik plan ve roadmap', en: 'MVP scope, technical plan, and roadmap' },
      { tr: 'Core kullanıcı akışları ve onboarding', en: 'Core user flows and onboarding' },
      { tr: 'SaaS backend, auth, billing, dashboard', en: 'SaaS backend, auth, billing, and dashboard' },
      { tr: 'Metrik altyapısı ve iterasyon backlog sistemi', en: 'Metrics instrumentation and iteration backlog model' },
    ],
    engagementModels: [
      { tr: 'MVP launch sprint modeli', en: 'MVP launch sprint model' },
      { tr: 'MVP sonrası growth + refactor fazı', en: 'Post-MVP growth and refactor phases' },
      { tr: 'Kurucu ekiplerle haftalık ürün ritmi', en: 'Weekly product cadence with founders' },
    ],
    process: [
      { title: { tr: 'Prioritize', en: 'Prioritize' }, description: { tr: 'Doğrulama için kritik özellikleri seçeriz.', en: 'We prioritize only validation-critical features.' } },
      { title: { tr: 'Build MVP', en: 'Build MVP' }, description: { tr: 'Hızlı ama sürdürülebilir temel ile ürünü geliştiririz.', en: 'We ship quickly on a maintainable foundation.' } },
      { title: { tr: 'Launch', en: 'Launch' }, description: { tr: 'İlk kullanıcı geri bildirimi toplayacak şekilde yayına alırız.', en: 'We launch with feedback loops enabled.' } },
      { title: { tr: 'Iterate', en: 'Iterate' }, description: { tr: 'Kullanım verisiyle roadmap’i güncelleriz.', en: 'We update roadmap using real usage data.' } },
    ],
    differentiators: [
      { tr: 'Startup hızını korurken teknik borcu kontrol altında tutarız', en: 'We preserve startup speed while controlling technical debt' },
      { tr: 'MVP sonrası ölçekleme planını baştan düşünürüz', en: 'We design with post-MVP scaling in mind' },
      { tr: 'Kurucu ekiplerle karar-temelli çalışma modeli sunarız', en: 'We run decision-driven collaboration with founders' },
    ],
    fitFor: [
      { tr: 'Hedef kullanıcı segmenti net olan girişimler', en: 'Startups with clear target user segment' },
      { tr: 'Geri bildirimle ürün evrimi yapmak isteyen ekipler', en: 'Teams committed to feedback-driven product evolution' },
    ],
    notFitFor: [
      { tr: 'Önceliklendirme olmadan sürekli kapsam genişleten projeler', en: 'Projects with constant scope expansion and no prioritization' },
      { tr: 'Sadece yatırım sunumu için görsel demo isteyen ekipler', en: 'Teams seeking only investor-facing visual demos' },
    ],
    buyerQueries: ['startup mvp builders', 'saas product engineering team', 'mvp development partner'],
    relatedProjectSlugs: ['ztech', 'kolektif'],
    relatedBlogSlugs: ['saas-development-cost-guide', 'saas-scaling-strategies', 'release-geometry-progressive-risk-slicing'],
    relatedToolIds: ['project-cost-estimator', 'pitch-evaluator', 'brand-kit-generator'],
    taxonomy: {
      capabilities: ['MVP Delivery', 'SaaS Architecture', 'Product Iteration'],
      buyerTypes: ['Founders', 'Early Product Teams', 'Startup CTOs'],
      projectTypes: ['Launch MVPs', 'B2B SaaS', 'Iteration Cycles'],
    },
    faqs: [
      {
        question: { tr: 'MVP kapsamını nasıl belirliyorsunuz?', en: 'How do you define MVP scope?' },
        answer: { tr: 'Doğrulanabilir kullanıcı değerine odaklanıp kritik olmayan kapsamı sonraki faza bırakıyoruz.', en: 'We focus on validation-critical user value and defer non-critical scope.' },
      },
      {
        question: { tr: 'MVP sonrası aynı ekip ile devam edebilir miyiz?', en: 'Can we continue with the same team after MVP?' },
        answer: { tr: 'Evet. Growth, teknik iyileştirme ve yeni özellik fazlarını aynı ekipte sürdürüyoruz.', en: 'Yes. We continue with growth, hardening, and feature delivery using the same team.' },
      },
      {
        question: { tr: 'Teknik olmayan kurucularla çalışma modeliniz nasıl?', en: 'How do you work with non-technical founders?' },
        answer: { tr: 'Teknik kararları net seçeneklere çevirip kurucu ekip için karar hızını artırıyoruz.', en: 'We turn technical complexity into clear options to improve founder decision speed.' },
      },
      {
        question: { tr: 'SaaS geliştirme ne kadar sürer ve ne kadar tutar?', en: 'How long does a SaaS MVP take, and what does it cost?' },
        answer: {
          tr: 'Maliyet tahmincimizdeki örnek bantlar: SaaS MVP {price:saas_mvp.mvp}, {duration:saas_mvp.mvp}; genişletilmiş platform {price:saas_mvp.platform}, {duration:saas_mvp.platform} (KDV hariç). Kesin fiyat ve takvim proje analizinden sonra iş listesi, efor ve kurulan outsource ekibe göre yazılı teklifte belirlenir.',
          en: 'Example bands from our cost estimator: SaaS MVP {price:saas_mvp.mvp} over {duration:saas_mvp.mvp}; expanded platform {price:saas_mvp.platform} over {duration:saas_mvp.platform} (excl. VAT). The final price and timeline are set in a written proposal after the project analysis, from the task list, the effort and the outsourced team assembled for it.',
        },
      },
      {
        question: { tr: 'Kaynak kod ve fikri mülkiyet kimde kalır?', en: 'Who owns the code and the intellectual property?' },
        answer: {
          tr: 'Kapsam, lisans ve fikri mülkiyet imzalı teklif ve hizmet sözleşmesinde açıkça belirlenir. Devirde kaynak kod, depo erişimi, teknik dokümantasyon ve hesap erişimlerinin devri yer alır; talep halinde gizlilik sözleşmesi (NDA) imzalanır.',
          en: 'Scope, licensing and intellectual property are set out in the signed proposal and service agreement. The handover includes source code, repository access, technical documentation and account access transfer; a non-disclosure agreement is available on request.',
        },
      },
      {
        question: { tr: 'Yurt dışındaki kurucularla çalışıyor musunuz?', en: 'Do you work with founders outside Turkey?' },
        answer: {
          tr: 'Evet. İstanbul’dan (UTC+3) Türkiye’nin tüm şehirlerindeki ve yurt dışındaki ekiplerle uzaktan, İngilizce veya Türkçe çalışıyoruz.',
          en: 'Yes. From Istanbul (UTC+3) we work remotely with teams across Turkey and in other countries, in English or Turkish.',
        },
      },
    ],
    landing: {
      quickAnswer: {
        tr: 'SaaS geliştirme; kullanıcı ve rol yönetimi, abonelik ve ödeme, çok kiracılı veri yapısı ve yönetim paneli olan bir web ürününü tasarlayıp yayına almaktır. Figtures önce kapsamı ve eforu çıkarır, ardından projeye özel outsource ekip kurar. Örnek SaaS MVP bandı {price:saas_mvp.mvp}, {duration:saas_mvp.mvp}; KDV hariçtir, kesin fiyat proje analizinden sonra yazılı teklifte belirlenir.',
        en: 'Figtures is a SaaS MVP development agency for founders and product teams. We scope the first version of your subscription product—sign-up, roles, billing, tenant data model and admin—then assemble an outsourced team sized to the effort. Example SaaS MVP band: {price:saas_mvp.mvp} over {duration:saas_mvp.mvp}, excl. VAT. The final price is set after project analysis, in a written proposal.',
      },
      costBands: [
        { profile: 'saas_mvp', scope: 'mvp' },
        { profile: 'saas_mvp', scope: 'platform' },
      ],
      caseStudies: ['p3', 'p10'],
      sections: [
        {
          title: { tr: 'SaaS MVP’ye genellikle neler girer?', en: 'What a SaaS MVP usually includes' },
          items: [
            { tr: 'Kayıt, giriş ve rol bazlı yetkilendirme', en: 'Sign-up, login and role-based permissions' },
            { tr: 'Plan yapısı ve ödeme entegrasyonu (çalıştığımız altyapılar: Stripe, iyzico, PayTR, Paddle)', en: 'Plan structure and payment integration (providers we work with: Stripe, iyzico, PayTR, Paddle)' },
            { tr: 'Müşteri (tenant) bazlı veri ayrımı', en: 'Per-customer (tenant) data separation' },
            { tr: 'Çekirdek değer akışı ve temel yönetim ekranları', en: 'The core value flow and basic admin screens' },
            { tr: 'Hata takibi ve ürün analitiği', en: 'Error tracking and product analytics' },
          ],
        },
        {
          title: { tr: 'İlk sürümde genellikle sonraya kalanlar', en: 'What usually waits for a later phase' },
          items: [
            { tr: 'Gelişmiş raporlama ve çoklu entegrasyonlar', en: 'Advanced reporting and multiple integrations' },
            { tr: 'Native mobil uygulamalar', en: 'Native mobile apps' },
            { tr: 'Kurumsal SSO ve özel sözleşme gereksinimleri (hedef segment gerektirmiyorsa)', en: 'Enterprise SSO and custom contract requirements (unless the target segment needs them)' },
          ],
        },
      ],
    },
  },
  {
    slug: 'mvp-development',
    shortLabel: { tr: 'MVP', en: 'MVP' },
    navTitle: { tr: 'MVP Geliştirme', en: 'MVP Development' },
    seoTitle: {
      tr: 'MVP Geliştirme | Web, Mobil ve SaaS MVP Hizmeti',
      en: 'MVP Development Services | Web, Mobile & SaaS MVPs',
    },
    seoDescription: {
      tr: 'MVP geliştirme: fikrinizi iş listesi, efor tahmini ve projeye özel ekiple test edilebilir ilk ürüne dönüştürüyoruz. Örnek fiyat/süre bandı, süreç ve SSS.',
      en: 'MVP development for founders and product teams: your idea scoped into a task list and effort estimate, then built by a project team. Example bands and FAQ.',
    },
    heroTitle: {
      tr: 'MVP geliştirme: fikrinizi gerçek kullanıcıyla test edilebilir ilk ürüne dönüştürüyoruz.',
      en: 'MVP development: we turn your idea into a first product real users can test.',
    },
    intro: {
      tr: 'Web, mobil veya SaaS fark etmeksizin, doğrulamak istediğiniz hipotezi taşıyan en küçük çalışan ürünü kapsamlıyor, geliştiriyor ve yayına alıyoruz.',
      en: 'Whether it is web, mobile or SaaS, we scope, build and launch the smallest working product that carries the hypothesis you want to validate.',
    },
    idealFor: [
      { tr: 'Fikrini ilk kullanıcılarla test etmek isteyen kurucular', en: 'Founders who want to test an idea with first users' },
      { tr: 'Yatırım görüşmesi öncesi çalışan ürün göstermek isteyen girişimler', en: 'Startups that want a working product before investor meetings' },
      { tr: 'Yeni bir ürün hattını küçük kapsamla denemek isteyen şirketler', en: 'Companies trialling a new product line with a small scope' },
      { tr: 'Teknik ekibi olmayan veya kapasitesi sınırlı ekipler', en: 'Teams without in-house engineers or with limited capacity' },
    ],
    problems: [
      { tr: 'Kapsam büyüdükçe ilk sürüm hiç yayına çıkmıyor', en: 'Scope keeps growing and the first version never ships' },
      { tr: 'Hangi özelliğin ilk sürüme gireceğine karar verilemiyor', en: 'No clear decision on what belongs in version one' },
      { tr: 'Hızlı çıkan prototip, gerçek kullanıcı gelince yeniden yazılmak zorunda kalıyor', en: 'A rushed prototype has to be rewritten once real users arrive' },
    ],
    deliverables: [
      { tr: 'Yazılı MVP kapsamı: kapsam içi ve dışı maddeler', en: 'A written MVP scope with in-scope and out-of-scope items' },
      { tr: 'Çekirdek kullanıcı akışları, arayüz tasarımı ve geliştirme', en: 'Core user flows, interface design and development' },
      { tr: 'Gerekli backend, yönetim ekranı ve entegrasyonlar', en: 'The backend, admin screens and integrations the MVP needs' },
      { tr: 'Yayın, ölçüm kurulumu ve devir dokümantasyonu', en: 'Launch, measurement setup and handover documentation' },
    ],
    engagementModels: [
      { tr: 'Sabit kapsamlı MVP projesi (yazılı teklifle)', en: 'Fixed-scope MVP project (with a written proposal)' },
      { tr: 'MVP sonrası iterasyon ve büyüme fazları', en: 'Post-MVP iteration and growth phases' },
      { tr: 'Mevcut ekibinizle birlikte çalışan proje ekibi', en: 'A project team working alongside your existing team' },
    ],
    process: [
      { title: { tr: 'Hipotez', en: 'Hypothesis' }, description: { tr: 'Doğrulanacak varsayımı ve başarı ölçütünü netleştiririz.', en: 'We pin down the assumption to validate and how success is measured.' } },
      { title: { tr: 'Kapsam', en: 'Scope' }, description: { tr: 'İlk sürüme girecek akışları seçer, gerisini sonraki faza yazarız.', en: 'We pick the flows for version one and park the rest for later phases.' } },
      { title: { tr: 'Geliştirme', en: 'Build' }, description: { tr: 'Sürdürülebilir bir temel üzerinde haftalık demolarla geliştiririz.', en: 'We build on a maintainable foundation with weekly demos.' } },
      { title: { tr: 'Yayın ve öğrenme', en: 'Launch and learn' }, description: { tr: 'Yayına alır, kullanım verisiyle bir sonraki adımı planlarız.', en: 'We launch and plan the next step from usage data.' } },
    ],
    differentiators: [
      { tr: 'Fiyatı iş listesi ve efordan hesaplar, yazılı teklifte gerekçelendiririz', en: 'We calculate the price from the task list and effort and explain it in the written proposal' },
      { tr: 'Projeye uygun rollerle outsource ekip kurarız; gereksiz kadro taşımazsınız', en: 'We assemble an outsourced team with the roles the project needs, so you do not carry idle headcount' },
      { tr: 'MVP’yi atılacak prototip değil, üzerine inşa edilecek temel olarak kurarız', en: 'We build the MVP as a foundation to build on, not a throwaway prototype' },
    ],
    fitFor: [
      { tr: 'Hedef kullanıcısı ve test etmek istediği varsayımı belli ekipler', en: 'Teams with a clear target user and an assumption to test' },
      { tr: 'İlk sürümü küçük tutup veriyle karar vermeye açık kurucular', en: 'Founders willing to keep version one small and decide with data' },
    ],
    notFitFor: [
      { tr: 'İlk sürümde tüm özellikleri isteyen, önceliklendirme yapmayan projeler', en: 'Projects that want every feature in version one without prioritising' },
      { tr: 'Yalnızca tıklanabilir görsel demo arayan talepler', en: 'Requests for a clickable visual demo only' },
    ],
    buyerQueries: ['mvp development company', 'mvp development services', 'build an mvp'],
    relatedProjectSlugs: ['backdoor-tickets', 'outfica'],
    relatedBlogSlugs: ['mvp-development-guide', 'mvp-development-company-guide', 'saas-development-cost-guide'],
    relatedToolIds: ['project-cost-estimator', 'pitch-evaluator', 'brand-kit-generator'],
    taxonomy: {
      capabilities: ['MVP Scoping', 'Product Design', 'Full-Stack Delivery'],
      buyerTypes: ['Founders', 'Product Owners', 'Innovation Teams'],
      projectTypes: ['Web MVPs', 'Mobile MVPs', 'SaaS MVPs'],
    },
    faqs: [
      {
        question: { tr: 'MVP nedir, prototipten farkı ne?', en: 'What is an MVP, and how is it different from a prototype?' },
        answer: {
          tr: 'MVP (minimum uygulanabilir ürün), gerçek kullanıcıların kullanabildiği, bir varsayımı test etmeye yetecek en küçük çalışan üründür. Prototip ise çoğunlukla tıklanabilir bir tasarımdır; veri kaydetmez ve canlı kullanıcıya açılmaz.',
          en: 'An MVP (minimum viable product) is the smallest working product that real users can use and that is enough to test an assumption. A prototype is usually a clickable design; it does not store data or go live to users.',
        },
      },
      {
        question: { tr: 'MVP geliştirme ne kadar sürer?', en: 'How long does MVP development take?' },
        answer: {
          tr: 'Kapsama bağlıdır. Maliyet tahmincimizdeki örnek sürelere göre bir SaaS MVP {duration:saas_mvp.mvp}, genel bir web/özel yazılım MVP’si {duration:general.mvp} sürer; bir mobil MVP ise projeye göre 1–2 haftadan 6–8 haftaya kadar sürer. Kesin takvim yazılı teklifte yer alır.',
          en: 'It depends on scope. Using the example timelines in our cost estimator, a SaaS MVP takes {duration:saas_mvp.mvp} and a general web/custom software MVP {duration:general.mvp}; a mobile MVP takes from 1–2 weeks to 6–8 weeks depending on the project. The exact timeline is in the written proposal.',
        },
      },
      {
        question: { tr: 'MVP geliştirme ne kadar tutar?', en: 'How much does MVP development cost?' },
        answer: {
          tr: 'Örnek bantlar (KDV hariç): SaaS MVP {price:saas_mvp.mvp}, mobil MVP {price:mobile_product.mvp}, genel / özel yazılım MVP {price:general.mvp}. Sabit fiyat listemiz yok; fiyat, proje analizinden sonra iş listesi, efor ve kurulan outsource ekibin maliyetinden hesaplanır.',
          en: 'Example bands (excl. VAT): SaaS MVP {price:saas_mvp.mvp}, mobile MVP {price:mobile_product.mvp}, general / custom software MVP {price:general.mvp}. We have no fixed price list; the price is calculated after the project analysis from the task list, the effort and the cost of the outsourced team.',
        },
      },
      {
        question: { tr: 'Teknik bilgim yok; MVP kapsamını nasıl belirleyeceğiz?', en: 'I am not technical; how do we decide the MVP scope?' },
        answer: {
          tr: 'Talebinizi dinledikten sonra ekran, akış ve entegrasyon düzeyinde bir iş listesi çıkarır, her kalemi kapsam içi veya sonraki faz olarak işaretleriz. Teknik kararları seçenek ve etkisiyle birlikte sade bir dille sunarız.',
          en: 'After listening to your request we write a task list at the level of screens, flows and integrations and mark each item as in scope or later phase. Technical decisions are presented as plain-language options with their impact.',
        },
      },
      {
        question: { tr: 'MVP sonrası aynı ekiple devam edebilir miyiz?', en: 'Can we continue with the same team after the MVP?' },
        answer: {
          tr: 'Evet. Yeni fazlar aynı teklif süreciyle kapsamlanır; isterseniz ürünü kendi ekibinize devrederiz. Devirde kaynak kod, depo erişimi ve dokümantasyon yer alır.',
          en: 'Yes. New phases are scoped through the same proposal process, or we hand the product over to your own team. The handover includes source code, repository access and documentation.',
        },
      },
      {
        question: { tr: 'Hangi sektörlerde ve şehirlerde MVP geliştiriyorsunuz?', en: 'Which sectors and locations do you build MVPs for?' },
        answer: {
          tr: 'Her sektörde çalışıyoruz. Merkezimiz İstanbul’da; Türkiye’nin tüm şehirlerine ve yurt dışına uzaktan hizmet veriyoruz.',
          en: 'We work across all sectors. We are based in Istanbul and serve every city in Turkey and clients abroad remotely.',
        },
      },
    ],
    landing: {
      quickAnswer: {
        tr: 'MVP geliştirme, bir ürün fikrini gerçek kullanıcılarla test edilebilecek en küçük çalışan sürüme dönüştürmektir. Figtures talebinizi dinler, iş listesini ve eforu çıkarır, projeye özel outsource ekip kurar ve yazılı teklif hazırlar. Örnek bant: SaaS MVP {price:saas_mvp.mvp}, {duration:saas_mvp.mvp}; mobil MVP projeye göre 1–2 haftadan 6–8 haftaya sürer. KDV hariçtir; kesin fiyat proje analizinden sonra belirlenir.',
        en: 'MVP development turns a product idea into the smallest working version real users can test. Figtures writes the task list and effort estimate, assembles an outsourced team and sends a written proposal. Example band: SaaS MVP {price:saas_mvp.mvp} over {duration:saas_mvp.mvp}; a mobile MVP takes 1–2 weeks to 6–8 weeks depending on the project. Excl. VAT; final price after project analysis.',
      },
      costBands: [
        { profile: 'saas_mvp', scope: 'mvp' },
        { profile: 'mobile_product', scope: 'mvp', duration: MOBILE_MVP_DURATION },
        { profile: 'general', scope: 'mvp' },
      ],
      caseStudies: ['p10', 'p3'],
      sections: [
        {
          title: { tr: 'MVP kapsamına genellikle neler girer?', en: 'What an MVP usually includes' },
          items: [
            { tr: 'Varsayımı test eden tek bir çekirdek kullanıcı akışı', en: 'One core user flow that tests the assumption' },
            { tr: 'Kayıt/giriş ve gerekli minimum yönetim ekranı', en: 'Sign-up/login and the minimum admin screen required' },
            { tr: 'Ürünün çalışması için şart olan entegrasyonlar (ör. ödeme)', en: 'Only the integrations the product cannot work without (e.g. payments)' },
            { tr: 'Kullanımı ölçmek için analitik ve hata takibi', en: 'Analytics and error tracking to measure usage' },
          ],
        },
        {
          title: { tr: 'Genellikle sonraki faza kalanlar', en: 'What usually waits for a later phase' },
          items: [
            { tr: 'İkinci ve üçüncü kullanıcı tipi için ayrı akışlar', en: 'Separate flows for a second or third user type' },
            { tr: 'Gelişmiş raporlama ve otomasyonlar', en: 'Advanced reporting and automations' },
            { tr: 'Doğrulama öncesi büyük ölçek mimarisi', en: 'Large-scale architecture before validation' },
          ],
        },
      ],
      guideSlugs: ['saas-mvp-development-turkey', 'mobile-app-development-turkey'],
    },
  },
  {
    slug: 'ui-ux-design',
    shortLabel: { tr: 'UI/UX Design', en: 'UI/UX Design' },
    navTitle: { tr: 'UI/UX Tasarım', en: 'UI/UX Design' },
    seoTitle: {
      tr: 'UI/UX Product Studio | Ürün Odaklı Tasarım ve Design Systems',
      en: 'UI/UX Product Studio | Product Design & Design Systems',
    },
    seoDescription: {
      tr: 'Figma-to-code design systems, kullanılabilirlik testleri ve dönüşüm odaklı arayüzler — mühendislikle aynı ritimde tasarım.',
      en: 'Figma-to-code design systems, usability testing, and conversion-focused interfaces—design in the same cadence as engineering.',
    },
    heroTitle: {
      tr: 'Kullanıcıların anlayacağı, ekiplerin sürdürebileceği ürün deneyimleri tasarlıyoruz.',
      en: 'We design product experiences users understand and teams can maintain.',
    },
    intro: {
      tr: 'Sadece ekran değil; karar akışları, bilgi mimarisi ve uygulanabilir design sistemi kuruyoruz.',
      en: 'We design not only screens, but decision flows, information architecture, and implementation-ready systems.',
    },
    idealFor: [
      { tr: 'UX borcu birikmiş ürün ekipleri', en: 'Product teams with accumulated UX debt' },
      { tr: 'Yeni ürün akışı tasarlayan startup’lar', en: 'Startups designing new product journeys' },
      { tr: 'Tasarım-geliştirme handoff sorunu yaşayan ekipler', en: 'Teams with design-engineering handoff gaps' },
    ],
    problems: [
      { tr: 'Karmaşık kullanıcı akışları ve düşük dönüşüm', en: 'Complex journeys and weak conversion' },
      { tr: 'Tutarsız arayüz ve ölçeklenemeyen tasarım dili', en: 'Inconsistent UI and non-scalable design language' },
      { tr: 'Ürün hedefiyle hizalanmayan tasarım kararları', en: 'Design decisions misaligned with product goals' },
    ],
    deliverables: [
      { tr: 'Bilgi mimarisi ve kullanıcı akışları', en: 'Information architecture and user flows' },
      { tr: 'Reusable UI sistemi', en: 'Reusable UI system' },
      { tr: 'Web + mobile ekran setleri', en: 'Web + mobile screen sets' },
      { tr: 'Dev-ready handoff ve iterasyon desteği', en: 'Dev-ready handoff and iteration support' },
    ],
    engagementModels: [
      { tr: 'Yeni ürün UX/UI tasarımı', en: 'New product UX/UI design' },
      { tr: 'Mevcut ürün redesign ve UX hardening', en: 'Existing product redesign and UX hardening' },
      { tr: 'Embedded design partner modeli', en: 'Embedded design partner model' },
    ],
    process: [
      { title: { tr: 'Research', en: 'Research' }, description: { tr: 'Kullanıcı ve ürün hedeflerini netleştiririz.', en: 'We clarify user and product goals.' } },
      { title: { tr: 'Structure', en: 'Structure' }, description: { tr: 'Akış ve ekran hiyerarşisini kurarız.', en: 'We structure flows and screen hierarchy.' } },
      { title: { tr: 'Design', en: 'Design' }, description: { tr: 'Uygulanabilir UI sistemi oluştururuz.', en: 'We create implementation-ready UI systems.' } },
      { title: { tr: 'Iterate', en: 'Iterate' }, description: { tr: 'Geri bildirimle tasarımı iyileştiririz.', en: 'We improve design with real feedback.' } },
    ],
    differentiators: [
      { tr: 'Tasarımı geliştirme gerçekliğiyle birlikte üretiriz', en: 'We design with engineering reality in mind' },
      { tr: 'Estetik kadar karar hızını da optimize ederiz', en: 'We optimize for decision speed, not just aesthetics' },
      { tr: 'Global ekiplerle async design review ritmine uygunuz', en: 'We support async design review for global teams' },
    ],
    fitFor: [
      { tr: 'Ürün metriklerini UX ile iyileştirmek isteyen ekipler', en: 'Teams improving product metrics through UX' },
      { tr: 'Design system standardı oluşturmak isteyen şirketler', en: 'Companies building design system standards' },
    ],
    notFitFor: [
      { tr: 'Sadece görsel makyaj isteyen ama ürün hedefi olmayan projeler', en: 'Visual polish requests without product goals' },
      { tr: 'Implementasyon kısıtlarını göz ardı eden süreçler', en: 'Processes that ignore implementation constraints' },
    ],
    buyerQueries: ['ui ux design partner', 'product design team for saas', 'web mobile ux agency'],
    relatedProjectSlugs: ['kolektif', 'ailem-aselsan'],
    relatedBlogSlugs: ['developer-experience-as-architecture', 'release-geometry-progressive-risk-slicing'],
    relatedToolIds: ['customer-reply-generator', 'color-palette-generator'],
    taxonomy: {
      capabilities: ['UX Strategy', 'UI Systems', 'Design for Conversion'],
      buyerTypes: ['Product Managers', 'Founders', 'Design + Engineering Leads'],
      projectTypes: ['Product Redesign', 'New Product UX', 'Design Systems'],
    },
    faqs: [
      {
        question: { tr: 'UI/UX tasarım sonrası geliştirme desteği de veriyor musunuz?', en: 'Do you support implementation after UI/UX design?' },
        answer: { tr: 'Evet. Tasarım ve mühendislik birlikte ilerlediği için handoff kaybını azaltıyoruz.', en: 'Yes. Design and engineering move together, reducing handoff friction.' },
      },
      {
        question: { tr: 'Tüm ürünü değil sadece kritik akışları yeniden tasarlayabilir misiniz?', en: 'Can you redesign only critical flows instead of the full product?' },
        answer: { tr: 'Evet. En yüksek etki üreten akışlara odaklanan hedefli redesign yapabiliyoruz.', en: 'Yes. We can run targeted redesign focused on highest-impact journeys.' },
      },
      {
        question: { tr: 'Design system kurulumunu da kapsıyor mu?', en: 'Do you include design-system setup?' },
        answer: { tr: 'Gereken projelerde component seviyesinde design system yaklaşımı kuruyoruz.', en: 'For suitable projects, we establish component-level design systems.' },
      },
    ],
  },
  {
    slug: 'ai-automation-agents',
    shortLabel: { tr: 'AI Automation', en: 'AI Automation' },
    navTitle: { tr: 'AI Otomasyon / Agent', en: 'AI Automation / Agents' },
    seoTitle: {
      tr: 'AI Otomasyon Product Studio | Agent Workflow ve İş Akışı Otomasyonu',
      en: 'AI Automation Product Studio | Agent Workflows & Business Automation',
    },
    seoDescription: {
      tr: 'Human-in-the-loop AI agent, ticket triage, doküman işleme ve operasyon otomasyonu — güvenli rollout ile.',
      en: 'Human-in-the-loop AI agents, ticket triage, document processing, and ops automation—with safe rollout.',
    },
    heroTitle: {
      tr: 'Manuel operasyonları AI automation ile sadeleştiriyoruz.',
      en: 'We streamline manual operations with AI automation.',
    },
    intro: {
      tr: 'Zaman tüketen süreçleri analiz edip kontrol mekanizmalı otomasyon sistemlerine dönüştürüyoruz.',
      en: 'We convert time-consuming workflows into controlled and observable automation systems.',
    },
    idealFor: [
      { tr: 'Operasyon tarafında yoğun manuel iş yükü olan ekipler', en: 'Teams with heavy manual operational workload' },
      { tr: 'Birden fazla aracı birlikte kullanan büyüyen şirketler', en: 'Growing teams running across multiple tools' },
      { tr: 'Ops ve teknik ekipler arasında handoff maliyeti yüksek organizasyonlar', en: 'Organizations with costly ops-engineering handoffs' },
    ],
    problems: [
      { tr: 'Tekrarlayan işlerin ekip kapasitesini tüketmesi', en: 'Repetitive tasks consuming team capacity' },
      { tr: 'Süreç bilgisinin kişilerde kalması', en: 'Process knowledge trapped in individuals' },
      { tr: 'İzlenebilir olmayan kırılgan otomasyonlar', en: 'Brittle automations without observability' },
    ],
    deliverables: [
      { tr: 'Agent tabanlı operasyon akışları', en: 'Agent-based operational flows' },
      { tr: 'Tool entegrasyonu ve orchestration katmanı', en: 'Tool integrations and orchestration layer' },
      { tr: 'Onay mekanizmalı otomasyon sistemi', en: 'Approval-aware automation system' },
      { tr: 'Monitoring, error handling ve fallback setup', en: 'Monitoring, error handling, and fallback setup' },
    ],
    engagementModels: [
      { tr: 'Pilot otomasyon sprinti', en: 'Pilot automation sprint' },
      { tr: 'Süreç bazlı fazlı dönüşüm', en: 'Phased process-by-process transformation' },
      { tr: 'Ops + teknik ekiplerle birlikte execution modeli', en: 'Joint execution model with ops and engineering teams' },
    ],
    process: [
      { title: { tr: 'Map', en: 'Map' }, description: { tr: 'Süreç adımlarını, karar noktalarını ve riskleri çıkarırız.', en: 'We map process steps, decision points, and risks.' } },
      { title: { tr: 'Automate', en: 'Automate' }, description: { tr: 'Agent ve workflow tasarımını kontrollü uygularız.', en: 'We implement agent/workflow logic with control gates.' } },
      { title: { tr: 'Validate', en: 'Validate' }, description: { tr: 'Doğruluk, hız ve hata senaryolarını test ederiz.', en: 'We validate accuracy, speed, and failure scenarios.' } },
      { title: { tr: 'Scale', en: 'Scale' }, description: { tr: 'Başarılı pilotları diğer süreçlere genişletiriz.', en: 'We scale validated pilots into additional workflows.' } },
    ],
    differentiators: [
      { tr: 'Agent’ları gerçek iş sürecine entegre ederiz', en: 'We integrate agents into real operations' },
      { tr: 'Kontrolsüz tam otonomi yerine güvenli insan-onay modeli kurarız', en: 'We favor safe human-in-the-loop control over brittle autonomy' },
      { tr: 'Global ekiplerle net dokümantasyonla ilerleriz', en: 'We collaborate with global teams through clear documentation' },
    ],
    fitFor: [
      { tr: 'Tekrarlı, kurallı operasyon süreçleri olan şirketler', en: 'Companies with repeatable, rules-based operations' },
      { tr: 'Operasyon verimliliğini sistematik artırmak isteyen ekipler', en: 'Teams targeting systematic operational efficiency gains' },
    ],
    notFitFor: [
      { tr: 'Süreç sahibi ve karar mekanizması tanımlanmamış organizasyonlar', en: 'Organizations without process ownership and decision model' },
      { tr: 'İzlenebilirlik olmadan kara kutu otomasyon talebi', en: 'Black-box automation requests without observability' },
    ],
    buyerQueries: ['ai automation partner', 'ai agent builders', 'workflow automation company'],
    relatedProjectSlugs: ['lcw', 'ztech'],
    relatedBlogSlugs: ['consistency-budgets-streaming-interfaces', 'evaluation-debt-in-ai-product-features'],
    relatedToolIds: ['roi-calculator', 'project-cost-estimator'],
    taxonomy: {
      capabilities: ['Agent Workflows', 'Automation Architecture', 'Operational AI'],
      buyerTypes: ['Operations Leads', 'CTOs', 'Automation Teams'],
      projectTypes: ['Internal Automation', 'Ops Copilots', 'Cross-tool Workflows'],
    },
    faqs: [
      {
        question: { tr: 'Agent sistemleri tamamen otonom mu oluyor?', en: 'Are agent systems fully autonomous?' },
        answer: { tr: 'Projeye göre değişir; çoğu durumda insan-onaylı güvenli otomasyon yaklaşımı kuruyoruz.', en: 'It depends; in most cases we implement safe human-in-the-loop automation.' },
      },
      {
        question: { tr: 'Mevcut araçlarımızla entegrasyon sağlıyor musunuz?', en: 'Do you integrate with our existing tools?' },
        answer: { tr: 'Evet. Kullanılan sistemleri analiz edip süreçe uygun entegrasyon katmanı kuruyoruz.', en: 'Yes. We analyze your stack and build integrations aligned with your workflow.' },
      },
      {
        question: { tr: 'Pilot ile başlayıp kademeli genişleyebilir miyiz?', en: 'Can we start with a pilot and scale gradually?' },
        answer: { tr: 'Evet. En yüksek etki üreten süreçle başlayıp doğrulama sonrası genişletiyoruz.', en: 'Yes. We start with a high-impact pilot and scale after validation.' },
      },
    ],
  },
  {
    slug: 'startup-product-engineering',
    shortLabel: { tr: 'Startup Engineering', en: 'Startup Engineering' },
    navTitle: { tr: 'Startup Ürün Mühendisliği', en: 'Startup Product Engineering' },
    seoTitle: {
      tr: 'Startup Product Studio | Erken Aşama Teknik Ürün Partneri',
      en: 'Startup Product Studio | Early-Stage Technical Product Partner',
    },
    seoDescription: {
      tr: 'Kurucu ekipler için embedded product engineering: discovery, MVP, fundraising demo ve production hardening tek çatı altında.',
      en: 'Embedded product engineering for founders: discovery, MVP, fundraising demos, and production hardening under one roof.',
    },
    heroTitle: {
      tr: 'Kurucu ekiplerle birlikte çalışan bir product engineering partneriyiz.',
      en: 'We are a product engineering partner built for founder teams.',
    },
    intro: {
      tr: 'Ürün kararları, önceliklendirme ve teknik teslimatı tek ritimde yürüten startup execution modeli sunuyoruz.',
      en: 'We provide startup execution that aligns product decisions, prioritization, and technical delivery.',
    },
    idealFor: [
      { tr: 'Teknik kurucu olmayan ama hızlı teslimat isteyen ekipler', en: 'Non-technical founders who need fast execution' },
      { tr: 'Erken aşama SaaS startup’ları', en: 'Early-stage SaaS startups' },
      { tr: 'İç kapasitesini senior execution ile büyütmek isteyen ekipler', en: 'Teams extending internal capacity with senior execution' },
    ],
    problems: [
      { tr: 'Ürün ve teknik kararların birbirinden kopması', en: 'Product and technical decisions drifting apart' },
      { tr: 'Roadmap ile teslimat arasında süreklilik kurulamaması', en: 'Roadmap and delivery becoming inconsistent' },
      { tr: 'Kurucu ekipte teknik belirsizlik ve karar yavaşlığı', en: 'Technical uncertainty slowing founder decisions' },
    ],
    deliverables: [
      { tr: 'Ürün scope ve delivery roadmap netliği', en: 'Product scope and delivery roadmap clarity' },
      { tr: 'Tasarım + geliştirme + lansman teslimatı', en: 'Design, engineering, and launch delivery' },
      { tr: 'Karar kayıtları ve teknik yönetişim ritmi', en: 'Decision logs and technical governance cadence' },
      { tr: 'Post-launch iyileştirme planı', en: 'Post-launch optimization plan' },
    ],
    engagementModels: [
      { tr: 'Fractional product engineering partner modeli', en: 'Fractional product engineering partner model' },
      { tr: 'Launch odaklı sprint blokları', en: 'Launch-focused sprint blocks' },
      { tr: 'Kurucu ekip ile haftalık review ritmi', en: 'Weekly review cadence with founders' },
    ],
    process: [
      { title: { tr: 'Align', en: 'Align' }, description: { tr: 'Kurucu hedefleri ve ürün önceliklerini hizalarız.', en: 'We align founder goals with product priorities.' } },
      { title: { tr: 'Execute', en: 'Execute' }, description: { tr: 'Kritik akışları iteratif şekilde teslim ederiz.', en: 'We deliver critical flows iteratively.' } },
      { title: { tr: 'Review', en: 'Review' }, description: { tr: 'Kullanıcı geri bildirimiyle roadmap’i güncelleriz.', en: 'We update roadmap based on user feedback.' } },
      { title: { tr: 'Scale', en: 'Scale' }, description: { tr: 'Ekip büyümesine uygun teknik temel kurarız.', en: 'We establish technical foundations for team scaling.' } },
    ],
    differentiators: [
      { tr: 'Teknik kararları kurucu ekip için anlaşılır hale getiririz', en: 'We translate technical decisions into founder-ready clarity' },
      { tr: 'Ürün, tasarım ve mühendisliği tek operasyon hattında birleştiririz', en: 'We unify product, design, and engineering in one execution lane' },
      { tr: 'Global/remote startup ekipleriyle operasyonel uyum sağlarız', en: 'We operate effectively with global remote startup teams' },
    ],
    fitFor: [
      { tr: 'Hızlı ama kontrollü ürün teslimatı isteyen startup’lar', en: 'Startups needing fast but controlled product delivery' },
      { tr: 'Kurucu seviyesinde karar netliği arayan ekipler', en: 'Teams seeking founder-level decision clarity' },
    ],
    notFitFor: [
      { tr: 'Sadece kısa süreli kod yazımı isteyen projeler', en: 'Ad-hoc coding requests without product context' },
      { tr: 'Önceliklendirme sürecine girmeyen ekipler', en: 'Teams unwilling to prioritize and decide' },
    ],
    buyerQueries: ['technical product partner', 'startup software team', 'product engineering for startups'],
    relatedProjectSlugs: ['ztech', 'kolektif'],
    relatedBlogSlugs: ['release-geometry-progressive-risk-slicing', 'developer-experience-as-architecture'],
    relatedToolIds: ['brand-kit-generator', 'project-cost-estimator', 'roi-calculator'],
    taxonomy: {
      capabilities: ['Startup Execution', 'Product Delivery', 'Technical Decision Support'],
      buyerTypes: ['Founders', 'Early Product Teams', 'Startup Operators'],
      projectTypes: ['MVP Launches', 'Iteration Cycles', 'PMF-Oriented Builds'],
    },
    faqs: [
      {
        question: { tr: 'Kurucu ekipte teknik lider yoksa nasıl ilerliyoruz?', en: 'How do we work if the founding team has no technical lead?' },
        answer: { tr: 'Teknik kararları sadeleştirip net seçenekler haline getirerek karar hızını artırıyoruz.', en: 'We simplify technical decisions into clear options to accelerate founder decision-making.' },
      },
      {
        question: { tr: 'Ürün stratejisine de katkı sağlıyor musunuz?', en: 'Do you contribute to product strategy too?' },
        answer: { tr: 'Evet. Scope, öncelik ve teslimat planını ürün hedefleriyle birlikte kurguluyoruz.', en: 'Yes. We align scope, prioritization, and delivery directly with product goals.' },
      },
      {
        question: { tr: 'Mevcut ekibimizle birlikte çalışabilir misiniz?', en: 'Can you work alongside our existing team?' },
        answer: { tr: 'Evet. İç ekip ritmine entegre olup kapasiteyi ve karar hızını birlikte artırıyoruz.', en: 'Yes. We embed into your existing cadence to increase delivery capacity and decision speed.' },
      },
    ],
  },
  {
    slug: 'nearshore-software-development-turkey',
    shortLabel: { tr: 'Nearshore', en: 'Nearshore' },
    navTitle: { tr: 'Nearshore Yazılım Geliştirme', en: 'Nearshore Development Turkey' },
    seoTitle: {
      tr: 'Nearshore Yazılım Geliştirme | Türkiye’den Avrupa’ya Ekip',
      en: 'Nearshore Software Development in Turkey | Figtures',
    },
    seoDescription: {
      tr: 'Yurt dışındaki şirketler için Türkiye’den nearshore yazılım geliştirme: UTC+3 mesai örtüşmesi, GDPR/KVKK veri işleme sözleşmesi, kod sahipliği ve projeye özel ekip.',
      en: 'Nearshore software development from Istanbul: UTC+3 overlap with EU hours, GDPR data processing agreements, clear code and IP ownership, English communication.',
    },
    heroTitle: {
      tr: 'Türkiye’den nearshore yazılım geliştirme: Avrupa mesaisiyle örtüşen, projeye özel ekip.',
      en: 'Nearshore software development in Turkey, with a project team that shares your working day.',
    },
    intro: {
      tr: 'Avrupa, Birleşik Krallık ve diğer ülkelerdeki şirketler için web, mobil, SaaS ve yapay zeka projelerini İstanbul’dan, İngilizce iletişim ve yazılı sözleşme çerçevesiyle geliştiriyoruz.',
      en: 'We build web, mobile, SaaS and AI products from Istanbul for companies in Europe, the UK and beyond, with English communication and a written contractual framework.',
    },
    idealFor: [
      { tr: 'Avrupa’da ürün geliştirme kapasitesini artırmak isteyen şirketler', en: 'European companies that need more product engineering capacity' },
      { tr: 'Teknik ekibi olmayan ve uzaktan bir geliştirme ortağı arayan kurucular', en: 'Founders without an in-house team looking for a remote development partner' },
      { tr: 'Offshore çalışmada saat farkı ve iletişim sorunu yaşamış ekipler', en: 'Teams that struggled with time zones and communication in offshore work' },
      { tr: 'Türkiye pazarına da ürün çıkarmak isteyen yabancı şirketler', en: 'International companies that also want to launch a product in Turkey' },
    ],
    problems: [
      { tr: 'Uzak saat dilimleri yüzünden kararlar bir gün gecikiyor', en: 'Distant time zones delay every decision by a day' },
      { tr: 'Kod sahipliği ve fikri mülkiyet sözleşmede belirsiz kalıyor', en: 'Code and IP ownership are left vague in the contract' },
      { tr: 'Fiyat, kapsam ve ekip yapısı başta yazılı hale gelmiyor', en: 'Price, scope and team structure are never written down up front' },
    ],
    deliverables: [
      { tr: 'İş listesi, efor tahmini ve fiyat içeren yazılı teklif', en: 'A written proposal with the task list, effort estimate and price' },
      { tr: 'Projeye özel kurulan outsource ekip ve tek iletişim noktası', en: 'An outsourced team assembled for the project and a single point of contact' },
      { tr: 'Gerektiğinde teknik doküman: mimari, entegrasyonlar, kabul kriterleri', en: 'A technical document when needed: architecture, integrations, acceptance criteria' },
      { tr: 'Kaynak kod, depo erişimi ve devir dokümantasyonu', en: 'Source code, repository access and handover documentation' },
    ],
    engagementModels: [
      { tr: 'Sabit kapsamlı proje (yazılı teklifle)', en: 'Fixed-scope project (with a written proposal)' },
      { tr: 'Fazlara bölünmüş ürün geliştirme', en: 'Phased product development' },
      { tr: 'Mevcut ekibinizle birlikte çalışan proje ekibi', en: 'A project team working alongside your in-house team' },
    ],
    process: [
      { title: { tr: 'Görüşme', en: 'Discovery call' }, description: { tr: 'Online toplantıda hedefleri, kısıtları ve zaman çizelgesini dinleriz.', en: 'We listen to your goals, constraints and timeline in an online call.' } },
      { title: { tr: 'Teklif', en: 'Proposal' }, description: { tr: 'İş listesi, efor, ekip ve fiyatı yazılı teklifte sunarız.', en: 'We present the task list, effort, team and price in a written proposal.' } },
      { title: { tr: 'Teslimat', en: 'Delivery' }, description: { tr: 'Ortak mesai saatlerinde demolar ve düzenli ilerleme raporlarıyla geliştiririz.', en: 'We build with demos in shared working hours and regular progress reports.' } },
      { title: { tr: 'Devir', en: 'Handover' }, description: { tr: 'Kaynak kodu, erişimleri ve dokümantasyonu devrederiz.', en: 'We hand over source code, access and documentation.' } },
    ],
    differentiators: [
      { tr: 'İstanbul yıl boyunca UTC+3: Orta Avrupa’nın 1–2 saat, Birleşik Krallık’ın 2–3 saat önünde', en: 'Istanbul is on UTC+3 all year: 1–2 hours ahead of Central Europe and 2–3 hours ahead of the UK' },
      { tr: 'Fiyat iş listesi ve efordan hesaplanır, yazılı teklifte gerekçelendirilir', en: 'The price is calculated from the task list and effort and explained in the written proposal' },
      { tr: 'Ekip her proje için ihtiyaç duyulan rollerle kurulur', en: 'The team is assembled for each project with the roles it needs' },
    ],
    fitFor: [
      { tr: 'Yazılı kapsam ve teklifle çalışmak isteyen şirketler', en: 'Companies that want to work from a written scope and proposal' },
      { tr: 'Haftalık demo ve ortak mesai saatlerinde iletişime açık ekipler', en: 'Teams open to weekly demos and communication during shared hours' },
    ],
    notFitFor: [
      { tr: 'Yalnızca saatlik ücretle bireysel geliştirici kiralamak isteyenler', en: 'Buyers looking only to hire individual developers by the hour' },
      { tr: 'Kapsam yazılmadan sabit fiyat bekleyen projeler', en: 'Projects expecting a fixed price without a written scope' },
    ],
    buyerQueries: ['nearshore software development', 'software development company turkey', 'outsource development turkey'],
    relatedProjectSlugs: ['backdoor-tickets', 'outfica'],
    relatedBlogSlugs: ['custom-software-development-company-guide', 'data-security-and-compliance', 'ai-development-company-selection-guide'],
    relatedToolIds: ['project-cost-estimator', 'quote-proposal-writer', 'pitch-evaluator'],
    taxonomy: {
      capabilities: ['Nearshore Delivery', 'Full-Stack Development', 'Product Engineering'],
      buyerTypes: ['European Companies', 'Founders', 'CTOs'],
      projectTypes: ['SaaS Products', 'Web & Mobile Apps', 'AI Features'],
    },
    faqs: [
      {
        question: { tr: 'Saat farkı nasıl yönetiliyor?', en: 'How does the time difference work?' },
        answer: {
          tr: 'İstanbul yıl boyunca UTC+3 saat dilimindedir; Orta Avrupa’nın 1–2 saat, Birleşik Krallık’ın 2–3 saat önündedir. Böylece iş gününün büyük bölümü örtüşür; toplantılar ve demolar ortak saatlerde planlanır.',
          en: 'Istanbul is on UTC+3 all year, 1–2 hours ahead of Central Europe and 2–3 hours ahead of the UK. Most of the working day overlaps, so meetings and demos are scheduled in shared hours.',
        },
      },
      {
        question: { tr: 'GDPR ve KVKK nasıl ele alınıyor?', en: 'How do you handle GDPR?' },
        answer: {
          tr: 'Projede kişisel veri işleniyorsa, sözleşmenin parçası olarak veri işleme sözleşmesi (DPA) imzalanır. Hangi verinin nerede tutulacağı ve barındırma bölgesi proje analizinde sizinle birlikte belirlenir.',
          en: 'Where the project involves personal data, a data processing agreement (DPA) is signed as part of the contract. Which data is stored where, and the hosting region, are decided with you during the project analysis.',
        },
      },
      {
        question: { tr: 'Kaynak kod ve fikri mülkiyet kime ait olur?', en: 'Who owns the code and the intellectual property?' },
        answer: {
          tr: 'Kapsam, lisans ve fikri mülkiyet imzalı teklif ve hizmet sözleşmesinde açıkça belirlenir. Devirde kaynak kod, depo erişimi, dokümantasyon ve hesap erişimlerinin devri yer alır; talep halinde gizlilik sözleşmesi (NDA) imzalanır.',
          en: 'Scope, licensing and intellectual property are set out explicitly in the signed proposal and service agreement. The handover includes source code, repository access, documentation and account access transfer; a non-disclosure agreement is available on request.',
        },
      },
      {
        question: { tr: 'İletişim hangi dilde yürüyor?', en: 'What language do you work in?' },
        answer: {
          tr: 'Toplantılar, yazılı iletişim, teklif ve teknik dokümantasyon İngilizce yürütülebilir; Türkçe de çalışıyoruz.',
          en: 'Meetings, written communication, proposals and technical documentation run in English. We also work in Turkish.',
        },
      },
      {
        question: { tr: 'Outsource ekip modeli nasıl çalışıyor?', en: 'How does the outsourced team model work?' },
        answer: {
          tr: 'Talebinizi dinler, iş listesini ve eforu çıkarırız. Ardından projenin ihtiyaç duyduğu rollerle (ör. tasarım, frontend, backend, mobil) outsource bir ekip kurar ve fiyatı bu ekibin maliyetinden hesaplarız. Tek iletişim noktanız Figtures olur.',
          en: 'We listen to your request and write the task list and effort estimate. We then assemble an outsourced team with the roles the project needs (for example design, frontend, backend, mobile) and calculate the price from that team’s cost. Figtures remains your single point of contact.',
        },
      },
      {
        question: { tr: 'Örnek fiyat bantları nedir?', en: 'What are the example price bands?' },
        answer: {
          tr: 'Maliyet tahmincimizdeki örnek bantlar (Türk lirası, KDV hariç): genel / özel yazılım MVP {price:general.mvp}, SaaS MVP {price:saas_mvp.mvp}. Kesin fiyat proje analizinden sonra yazılı teklifte belirlenir.',
          en: 'Example bands from our cost estimator (Turkish lira, excl. VAT): general / custom software MVP {price:general.mvp}, SaaS MVP {price:saas_mvp.mvp}. The final price is set in a written proposal after the project analysis.',
        },
      },
    ],
    landing: {
      quickAnswer: {
        tr: 'Nearshore yazılım geliştirme, yakın saat dilimindeki bir ekiple uzaktan ürün geliştirmektir. Figtures İstanbul’dadır (UTC+3, Orta Avrupa’nın 1–2 saat önünde); İngilizce iletişim kurar, gerektiğinde GDPR/KVKK veri işleme sözleşmesi imzalar, kod ve fikri mülkiyeti sözleşmede netleştirir. Talebinizi dinler, iş listesini ve eforu çıkarır, projeye özel outsource ekip kurar ve yazılı teklif sunarız.',
        en: 'Nearshore software development means working with a remote team in a nearby time zone. Figtures works from Istanbul (UTC+3, 1–2 hours ahead of Central Europe) in English. We sign a GDPR data processing agreement where personal data is involved and set code and IP ownership in the contract. Scope, outsourced team and price come in a written proposal.',
      },
      costBands: [
        { profile: 'general', scope: 'mvp' },
        { profile: 'saas_mvp', scope: 'mvp' },
        { profile: 'general', scope: 'platform' },
      ],
      costBandNote: {
        tr: 'Bantlar maliyet tahmincimizin kullandığı para birimi olan Türk lirası (TRY) cinsindendir.',
        en: 'Bands are shown in Turkish lira (TRY), the currency our cost estimator uses.',
      },
      caseStudies: ['p10', 'p3'],
      sections: [
        {
          title: { tr: 'Neden Türkiye’den nearshore?', en: 'Why nearshore in Turkey' },
          items: [
            { tr: 'Saat dilimi: İstanbul yıl boyunca UTC+3; Orta Avrupa’nın 1–2 saat, Birleşik Krallık’ın 2–3 saat önünde', en: 'Time zone: Istanbul is on UTC+3 all year, 1–2 hours ahead of Central Europe and 2–3 hours ahead of the UK' },
            { tr: 'İletişim: toplantılar, teklif ve dokümantasyon İngilizce', en: 'Communication: meetings, proposals and documentation in English' },
            { tr: 'Ekip modeli: her proje için ihtiyaç duyulan rollerle kurulan outsource ekip, tek iletişim noktası', en: 'Team model: an outsourced team assembled per project with the roles it needs, one point of contact' },
          ],
        },
        {
          title: { tr: 'Sözleşme, veri ve sahiplik', en: 'Contract, data and ownership' },
          items: [
            { tr: 'Kişisel veri işleniyorsa GDPR/KVKK veri işleme sözleşmesi (DPA)', en: 'A GDPR data processing agreement (DPA) where personal data is processed' },
            { tr: 'Kod, lisans ve fikri mülkiyet imzalı teklif ve hizmet sözleşmesinde yazılı', en: 'Code, licensing and IP ownership written into the signed proposal and service agreement' },
            { tr: 'Devirde kaynak kod, depo erişimi ve dokümantasyon; talep halinde NDA', en: 'Source code, repository access and documentation at handover; NDA on request' },
            { tr: 'Barındırma bölgesi proje analizinde sizinle birlikte seçilir', en: 'The hosting region is chosen with you during the project analysis' },
          ],
        },
      ],
      guideSlugs: ['custom-software-development-turkey', 'ai-development-company-turkey'],
    },
  },
];

const services: ServiceDefinition[] = serviceDefinitions.map((service) => ({
  ...service,
  faqs: resolveLandingFaqs(service.faqs),
}));

const serviceMap = new Map(services.map((service) => [service.slug, service]));

const relatedServiceMap: Record<string, string[]> = {
  'ztech': ['ai-development', 'saas-mvp-development', 'startup-product-engineering'],
  'ailem-aselsan': ['custom-software-development', 'mobile-app-development', 'ui-ux-design'],
  kolektif: ['mobile-app-development', 'ui-ux-design', 'startup-product-engineering'],
  lcw: ['ai-automation-agents', 'custom-software-development', 'web-development'],
  'evaluation-debt-in-ai-product-features': ['ai-development', 'ai-automation-agents'],
  'consistency-budgets-streaming-interfaces': ['ai-development', 'web-development'],
  'api-contract-evolution-without-breakage': ['custom-software-development', 'web-development'],
  'offline-first-mobile-sync-conflict-discipline': ['mobile-app-development', 'startup-product-engineering'],
  'release-geometry-progressive-risk-slicing': ['saas-mvp-development', 'startup-product-engineering'],
  'developer-experience-as-architecture': ['custom-software-development', 'ui-ux-design'],
  'ux-design-in-ai-products': ['ai-development', 'ui-ux-design', 'startup-product-engineering'],
  'saas-scaling-strategies': ['saas-mvp-development', 'custom-software-development', 'startup-product-engineering'],
  'modern-fullstack-tooling': ['custom-software-development', 'web-development', 'startup-product-engineering'],
  'mvp-development-guide': ['mvp-development', 'saas-mvp-development', 'startup-product-engineering'],
  'mobile-app-performance-optimization': ['mobile-app-development', 'custom-software-development', 'web-development'],
  'data-security-and-compliance': ['custom-software-development', 'ai-development', 'nearshore-software-development-turkey'],
  'custom-software-development-company-guide': [
    'custom-software-development',
    'nearshore-software-development-turkey',
    'web-development',
  ],
  'ai-development-company-selection-guide': ['ai-development', 'ai-automation-agents', 'startup-product-engineering'],
  'saas-development-cost-guide': ['saas-mvp-development', 'mvp-development', 'custom-software-development'],
  'mobile-app-development-cost-guide': ['mobile-app-development', 'custom-software-development', 'ui-ux-design'],
  'chatgpt-integration-business-guide': ['ai-development', 'ai-automation-agents', 'custom-software-development'],
  'mvp-development-company-guide': ['mvp-development', 'saas-mvp-development', 'startup-product-engineering'],
};

export function getAllServices() {
  return services;
}

export function getServiceBySlug(slug: string) {
  return serviceMap.get(slug) || null;
}

export function listServiceDefinitions() {
  return services;
}

export function getServicesBySlugs(slugs: string[]) {
  return slugs
    .map((slug) => getServiceBySlug(slug))
    .filter((service): service is ServiceDefinition => Boolean(service));
}

export function getFeaturedServiceSlugs() {
  return ['ai-development', 'custom-software-development', 'saas-mvp-development', 'mobile-app-development'];
}

/** Default homepage service cards when CMS `home.services` is empty. */
export function getHomepageServicesSectionSlugs() {
  return [
    'ai-development',
    'saas-mvp-development',
    'ui-ux-design',
    'web-development',
    'mobile-app-development',
    'startup-product-engineering',
  ];
}

/** CMS card titles carry no slug, so the target service is derived from the title (TR + EN), never from array position. */
const homepageCardTitleRules: Array<{ slug: string; pattern: RegExp }> = [
  { slug: 'ai-automation-agents', pattern: /\b(automation|otomasyon|agents?)\b/i },
  { slug: 'saas-mvp-development', pattern: /\b(saas|mvp)\b/i },
  { slug: 'mobile-app-development', pattern: /\b(mobile|mobil)\b/i },
  { slug: 'web-development', pattern: /(full[\s-]?stack|\bweb\b)/i },
  { slug: 'ui-ux-design', pattern: /(ui\s*\/\s*ux|\bux\b|product design|ürün tasarım|\bdesign\b|tasarım)/i },
  { slug: 'custom-software-development', pattern: /(custom software|özel yazılım)/i },
  { slug: 'startup-product-engineering', pattern: /(consult|danışman|startup)/i },
  { slug: 'ai-development', pattern: /(\bai\b|yapay zek|\bllm\b)/i },
];

export function resolveHomepageServiceSlugByTitle(title: string): string | null {
  const rule = homepageCardTitleRules.find(({ pattern }) => pattern.test(title));
  return rule ? rule.slug : null;
}

export function resolveHomepageServiceCards(
  lang: Locale,
  cmsServices?: Array<{ title: string; description: string }>,
) {
  if (!cmsServices?.length) {
    return getHomepageServicesSectionSlugs()
      .map((slug) => getServiceBySlug(slug))
      .filter((service): service is ServiceDefinition => Boolean(service))
      .map((service) => ({
        slug: service.slug,
        title: localizeServiceText(service.navTitle, lang),
        description: localizeServiceText(service.intro, lang),
      }));
  }

  return cmsServices.map((cms) => {
    const slug = resolveHomepageServiceSlugByTitle(cms.title);
    return {
      slug: slug && getServiceBySlug(slug) ? slug : null,
      title: cms.title,
      description: cms.description,
    };
  });
}

export function getRelatedServiceSlugsByEntitySlug(entitySlug: string) {
  return relatedServiceMap[entitySlug] || [];
}

export function getRelatedBlogSlugsByProjectSlug(projectSlug: string) {
  const slugSet = new Set<string>();
  for (const service of services) {
    if (!service.relatedProjectSlugs.includes(projectSlug)) continue;
    for (const blogSlug of service.relatedBlogSlugs) {
      slugSet.add(blogSlug);
    }
  }
  return Array.from(slugSet);
}

export function getRelatedServiceSlugsForProject(projectSlug: string) {
  const slugSet = new Set<string>();
  for (const service of services) {
    if (service.relatedProjectSlugs.includes(projectSlug)) {
      slugSet.add(service.slug);
    }
  }
  if (slugSet.size === 0) {
    return ['custom-software-development', 'startup-product-engineering'];
  }
  return Array.from(slugSet);
}

export function getRelatedServiceSlugsForBlog(params: {
  slug: string;
  title?: string;
  excerpt?: string;
  tags?: string[];
  category?: string;
}) {
  if (relatedServiceMap[params.slug]) return relatedServiceMap[params.slug];

  const haystack = [params.title, params.excerpt, params.category, ...(params.tags || [])]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  const scored = services
    .map((service) => {
      const score = service.buyerQueries.reduce((acc, query) => {
        const token = query.toLowerCase().split(' ')[0];
        return haystack.includes(token) ? acc + 1 : acc;
      }, 0);
      return { slug: service.slug, score };
    })
    .filter((item) => item.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 3)
    .map((item) => item.slug);

  return scored.length > 0 ? scored : ['custom-software-development', 'web-development'];
}

export function getRelatedServiceSlugsForTool(toolId: string) {
  const fromCatalog = services
    .filter((service) => service.relatedToolIds.includes(toolId))
    .map((service) => service.slug);

  return fromCatalog.length > 0 ? fromCatalog.slice(0, 3) : ['ai-development', 'saas-mvp-development'];
}
